// Configuración → Procesos (client-ops §3, PR 17): the funnel itself, editable.
//
// The funnel used to be a constant in three agreeing places, then (PR 11) two
// database tables with no write API and no screen. This is the screen. One
// funnel is shown at a time — a picker at the top, its stages underneath —
// because a tenant works one board at a time and stacking every pipeline's
// columns on one page is how "which Nuevo is this?" happens.
//
// Three rules run through the whole thing:
//
//   - the screen teaches its own model. A stage owned by the Agente is worked
//     by the IA; a stage owned by people hands the conversation over. That is
//     the single idea the whole tab exists to express, so it is said in words
//     at the top and again in the description of every choice that changes it.
//   - a delete that WOULD be refused is still offered, and answers with the
//     RULE before the request leaves the browser — never with the API's 409,
//     and never as ambient text sitting under a row nobody asked. The counts
//     the reasons quote come from the same read the API guards its 409s with,
//     and `pipelineDeleteBlocker` / `stageDeleteBlocker` mirror the API's guard
//     ORDER, so the refusal the tenant reads and the server's can never
//     disagree about which rule stopped you.
//   - the cita-agendada marker is one-per-funnel AND optional, so it is edited
//     ON the funnel: one Select (open stages + "Ninguna") up in the process
//     block. The wire is unchanged — moving the marker is turning it on in the
//     target stage, and the server clears the previous holder in the same
//     transaction. (F20: the capture marker is gone; new prospects enter the
//     first open stage.)
//
// This file keeps the page composition and the state several cards share (the
// selected funnel, its board, the lead-field library, the shared `run`). The
// cards that read as their own screens live next door: `AgentStrip` (lo que es
// del AGENTE y no de un proceso), `StageList` (las etapas y sus diálogos) y
// `AgentPreviewCard` (así quedaría tu agente EN ESTE proceso, y el Publicar que
// lo escribe).
//
// El ORDEN es el argumento (F4-2): arriba la tira del agente, que no cambia con
// el proceso; debajo, la zona de UN proceso, y todo lo que vive ahí sigue al
// selector. Mezclados en una sola pila, lo del agente y lo del proceso se leían
// como dos sistemas sin relación.
//
// Manage-only, like the tab that mounts it. The API refuses an Asesor anyway;
// the tab simply isn't offered to them.

import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  type AgentTerminal,
  captureCategoryLabelFor,
  ENTRY_LABEL_CONTROL_TERM,
  promptForField,
} from '@vysto/shared/compiler'
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'

import { routeTest } from './agent-preview-data.js'
import { AgentPreviewCard } from './AgentPreviewCard.js'
import { AgentStrip } from './AgentStrip.js'
import { OptionsSourceSelect, SourcedOptionsList } from './FieldOptionsSource.js'
import {
  createField,
  DATA_TYPE_LABELS,
  deleteField,
  type FieldDataType,
  type FieldDefinition,
  type FieldOptionsSource,
  listFields,
  slugifyKey,
  updateField,
} from './fields-data.js'
import {
  createPipeline,
  createStage,
  deletePipeline,
  deleteStage,
  getPipelines,
  pipelineDeleteBlocker,
  type PipelineStageView,
  readPipelineEnding,
  type PipelineFieldSave,
  type PipelineFieldView,
  type PipelineVisibilityPolicy,
  type PipelineView,
  type StageEdit,
  stagesRequiring,
  updatePipeline,
  updatePipelineFields,
  updateStage,
} from './pipelines-data.js'
import { ReorderControl } from './ReorderControl.js'
import { StageList } from './StageList.js'
import {
  Button,
  Chip,
  DropdownMenu,
  FormField,
  Input,
  type MenuItem,
  Modal,
  Select,
  Spinner,
  Textarea,
  toast,
} from '../../design-system/index.js'
import { apiErrorMessage } from '../../lib/api-error.js'
import { QueryError } from '../../lib/QueryError.js'
import { getWorkers } from '../workers/workers-data.js'

/** La etapa de cita agendada es un hecho DEL PROCESO, una por proceso y
 *  opcional: por eso se elige aquí, nombrando la etapa, y no dentro de una
 *  etapa cualquiera. El texto dice lo que hace de verdad. */
const COMMITMENT_DESCRIPTION =
  'Al agendar una cita el prospecto llega a esta etapa, deja de aparecer sin seguimiento y cuenta como cita agendada en los reportes.'
/** Lo que cambia al elegir "Ninguna", dicho solo cuando es el caso: agendar
 *  deja de mover la tarjeta y el KPI de citas se oculta para este proceso. */
const NO_COMMITMENT_HINT =
  'Con "Ninguna", agendar una cita no mueve al prospecto y el indicador de citas no se muestra para este proceso.'
/** Y por qué el Select está apagado cuando una etapa AGENDA (D22-9). La marca no
 *  se elige aparte: la lleva la columna donde el Agente reserva, porque ahí es
 *  donde la promesa ocurre y la base de datos no deja que sean dos. */
const COMMITMENT_FOLLOWS_AGENDA = 'La marca sigue a la etapa que agenda la cita.'
/** `''` en el Select de cita agendada: "ninguna etapa la tiene". */
const NO_STAGE = ''

/** Qué es la ficha, dicho en una línea antes de pedir nada: quién la llena,
 *  quién la edita y para qué sirve (CP3 K). Las definiciones siguen siendo del
 *  ESPACIO (una biblioteca, un significado por concepto); lo que se elige aquí
 *  es cuáles lleva ESTE proceso. Desde CP3 L también se editan desde aquí: la
 *  vieja pestaña Campos se quedó solo con las personas. */
// W4'-6c: las flechas de orden existían sin decir qué mueve el orden. Lo que
// mueve es REAL: `questionKeysFor` (@vysto/shared/compiler/capture-plan.ts)
// pregunta lo que cada etapa declara EN EL ORDEN DE LA FICHA, así que la frase
// promete exactamente lo que el compilador hace.
const FICHA_INTRO =
  'Los datos que llevan los prospectos de este proceso. El Agente los captura, tu equipo los edita, y las etapas pueden requerirlos. Su orden es el orden en que el agente los pregunta dentro de su etapa.'
/** LA FRASE DE CADA DATO (F4-4). Juan: "¿qué tal que yo quiero que se pregunte
 *  de otra manera?". La pregunta dejó de escribirla el generador: se escribe en
 *  el renglón del dato, y el flujo sale de ahí. La etiqueta pregunta lo que el
 *  campo hace, no cómo se llama el campo. */
const FICHA_PROMPT_LABEL = '¿Cómo lo pregunta el agente?'
/** CÓMO RECONOCE EL AGENTE ESTE PROCESO (F4-5). Juan: "¿por qué no puedo
 *  configurar cómo el agente reconoce cada proceso?". La opción del menú dejó de
 *  escribirla el compilador: se escribe aquí, en el bloque del proceso, y de
 *  aquí sale el menú del Agente. */
// El nombre del control sale de `@vysto/shared/compiler` (F4-8): el recibo de la
// captura lo NOMBRA ("la editas en …") para que la etiqueta de entrada se lea
// como configuración del cliente, así que una copia del literal aquí mandaría a
// buscar un control que ya se llama de otra manera.
//
// Y DESDE W4'-6c LLEVA SU CONCEPTO DELANTE. El intro del grupo habla de la
// ETIQUETA y el SIGNIFICADO, pero ningún control cargaba esas palabras: Juan,
// CP-4: "si yo no lo entiendo, nadie lo entiende". El prefijo se antepone al
// término compartido (con su inicial en minúscula) en vez de copiarlo, para que
// el recibo del servidor y este control sigan diciendo la misma frase.
const ENTRY_LABEL_LABEL = `Etiqueta: ${ENTRY_LABEL_CONTROL_TERM.charAt(0).toLowerCase()}${ENTRY_LABEL_CONTROL_TERM.slice(1)}`
/** LO QUE UN RECORTE DEJA DE EXIGIR, dicho sin prometer lo que todavía no pasa
 *  (F4-8). Ocultar o eliminar un dato decía "Dejó de pedirse en: …", que se lee
 *  como "el agente ya no lo pregunta". No es cierto en ese momento: el flujo
 *  publicado lo sigue preguntando hasta que se actualiza, y eso lo dice la
 *  deriva de la tarjeta de captura ("El flujo pregunta X, que ya no está en la
 *  ficha"), que se queda en pantalla en vez de irse como un toast. Lo que SÍ
 *  cambia en el instante es la EXIGENCIA de la etapa, y es lo único que se dice.
 *  Una sola frase para los tres recortes: las tres decían lo mismo de dos
 *  maneras. */
const STRIPPED_LEAD = 'Dejó de ser requerido en:'
/** EL EJEMPLO VA EN LA AYUDA, NO EN EL PLACEHOLDER (F6-10). Juan, con los dos
 *  campos ya llenos: "necesitaria un ejemplo de como seria correcto
 *  configurarlos y supongo que cualquier persona igual". Un placeholder no
 *  sirve para eso — sobre un campo escrito nunca se ve —, así que el ejemplo se
 *  integra en la ayuda que ya está debajo del control, en vez de apilar una
 *  segunda línea. El ejemplo de la etiqueta va en TERCERA persona, la misma
 *  forma que escribe `captureCategoryLabelFor`, para que se lea como lo que es:
 *  una opción del menú del agente.
 *
 *  Y DESDE W4'-6c LA AYUDA DICTA LA CONVENCIÓN, no sólo el ejemplo (CP-4: Juan
 *  metió una lista separada por comas y el recibo del publicar la enseñó rota
 *  como opción del menú). Contesta sus preguntas donde se escriben: una sola
 *  oración con un solo propósito, en tercera persona del presente y con la
 *  persona interesada como sujeto; no una lista de palabras; y vacía se deriva
 *  del nombre del proceso, que es válido y lo habitual para el principal. */
const ENTRY_LABEL_HELPER =
  'Una sola oración con un solo propósito, en tercera persona del presente y con la persona ' +
  'interesada como sujeto: "Quiere agendar una cita con un especialista". No es una lista de ' +
  'palabras clave. Si se deja vacía, se deriva del nombre del proceso; eso es válido y es lo ' +
  'habitual para el proceso principal.'
/** El tope de la columna `pipelines.entry_label`. Se pone en el input para que
 *  no se pueda teclear de más, y el servidor lo vuelve a decir en palabras por
 *  si la petición no viene de esta pantalla. */
const ENTRY_LABEL_MAX = 120

/** LA FORMA DE LA ETIQUETA SE AVISA, NO SE BLOQUEA (W4'-6c U1b). El dato real
 *  de Juan ("Estudios de laboratorio, estudios clínicos, examenes...") se rindió
 *  roto como opción del menú en el recibo del publicar, y nada se lo dijo al
 *  escribirlo. Dos comas o un largo de párrafo huelen a lista o a significado
 *  metido en el campo equivocado; se dice debajo, sin impedir guardar, porque
 *  una oración legítima también puede llevar comas. */
const ENTRY_LABEL_WARN_COMMAS = 2
const ENTRY_LABEL_WARN_LENGTH = 90
const ENTRY_LABEL_SHAPE_WARNING =
  'Una lista separada por comas se lee mal como opción del menú: deja aquí una sola oración y ' +
  'escribe las variantes en el significado.'
function entryLabelShapeWarns(value: string): boolean {
  const commas = value.split(',').length - 1
  return commas >= ENTRY_LABEL_WARN_COMMAS || value.length > ENTRY_LABEL_WARN_LENGTH
}

/** QUÉ SIGNIFICA ESTE PROCESO (F5-2). Juan, CP-4': "no tenemos manera de decir
 *  el significado de un proceso… en ningún lado especificamos este significado
 *  del proceso para que… una inteligencia artificial capture la intención de un
 *  lead de ese significado". La etiqueta de arriba es lo que el agente DICE;
 *  esto es lo que ENTIENDE, y por eso son dos controles y no uno: escribir un
 *  significado en la etiqueta obliga a decir en la conversación una frase
 *  escrita para el modelo. El nombre del control sale de
 *  `@vysto/shared/compiler` por lo mismo que el de la etiqueta: el rechazo del
 *  servidor lo NOMBRA.
 *
 *  DESDE W4'-6c EL CONCEPTO VA DELANTE, como en la etiqueta. Aquí el literal se
 *  reescribe en vez de derivarse: "Significado: qué significa este proceso"
 *  diría dos veces lo mismo, y "qué resuelve" es la mitad que el término
 *  compartido no dice. La palabra "significa" del rechazo del servidor sigue
 *  apuntando a "Significado" sin ambigüedad: no hay otro control que la lleve. */
const MEANING_LABEL = 'Significado: qué resuelve este proceso'
/** Su ejemplo es el contrapunto del de la etiqueta (F6-10): el de arriba se lee
 *  como una opción de menú, este describe la intención y la dice con OTRAS
 *  palabras, que es justo el trabajo que hace el significado.
 *
 *  W4'-6c: la ayuda también dicta la forma (la necesidad en una o dos
 *  oraciones, ejemplos citados al final) y contesta la pregunta de Juan que
 *  faltaba: la persona NO tiene que escribir la frase exacta. Los mensajes
 *  entre comillas son de clientes finales y conservan su registro natural. */
const MEANING_HELPER =
  'Describe la necesidad en una o dos oraciones: "La persona busca atenderse con un doctor o ' +
  'resolver un malestar". Puede cerrar con ejemplos citados de mensajes reales: "quiero ver a un ' +
  'cardiólogo", "necesito que me revisen". No es necesario que la persona escriba la frase exacta.'
/** El tope de la columna `pipelines.meaning`. Como con la etiqueta, se pone en
 *  el control para que no se pueda teclear de más, y el servidor lo vuelve a
 *  decir en palabras por si la petición no viene de esta pantalla. */
const MEANING_MAX = 500

/** CÓMO ENTRA LA GENTE A ESTE PROCESO (F6-2). Juan, CP-5': "falta un poco de
 *  claridad entre cómo lo reconoce el agente y qué significa el proceso, no sé
 *  bien cómo usar ambos campos". Y su dato lo probó: metió una LISTA de palabras
 *  clave en la etiqueta, que es el trabajo del significado. La etiqueta (F4-5) y
 *  el significado (F5-2) son las dos mitades de la misma puerta, así que van
 *  bajo un solo título con una línea que reparte el trabajo: la ETIQUETA es lo
 *  que el agente ofrece en su menú, el SIGNIFICADO es lo que entiende. El orden
 *  es el argumento: primero lo que se ve en el menú, luego lo que reconoce por
 *  debajo. Cada campo conserva su propia ayuda; esta línea sólo los enmarca.
 *
 *  W4'-6c: EL INTRO DICE LA MECÁNICA, en tres oraciones y no en una etiqueta de
 *  conceptos. El veredicto de CP-4 fue que "de qué trata" no alcanza para
 *  configurar el enrutamiento: hay que decir QUIÉN consume cada campo (el menú
 *  del agente / la comparación de la inteligencia) y que la comparación nunca
 *  exige la frase exacta, que era la duda que dejaba a Juan metiendo listas de
 *  palabras clave. */
const ENTRY_SECTION_TITLE = 'Cómo entra la gente a este proceso'
const ENTRY_SECTION_INTRO =
  'La etiqueta es la opción que el agente presenta en su menú. El significado es la descripción ' +
  'que la inteligencia compara contra cada mensaje para decidir a qué proceso entra. La ' +
  'comparación nunca exige la frase exacta.'

/** EL PROBADOR DE RUTEO (W4'-7, D25). El veredicto de CP-4: la etiqueta y el
 *  significado ya tienen su contrato en pantalla, pero nada contestaba "¿lo
 *  configuré bien?" salvo publicar y conversar. Aquí se escribe una frase y el
 *  clasificador REAL contesta a qué proceso u opción base entraría — el mismo
 *  mecanismo, las mismas opciones compiladas. La ORACIÓN del resultado viene
 *  del servidor y se pinta tal cual: la pantalla no redacta ninguna. */
const TESTER_LABEL = 'Prueba una frase'
const TESTER_ERROR = 'No pudimos probar la frase.'

/** ¿CÓMO TERMINA ESTE PROCESO? (F4-6). Juan, punto 8 de su aprobación del
 *  2026-09-12: "el final como control visible". El final SIEMPRE estuvo escrito
 *  en las etapas — una columna de traspaso después de la captura entrega la
 *  conversación, una columna final se despide, y sin ninguna de las dos el
 *  agente se queda contestando — y la pantalla nunca lo decía. Aquí se lee en
 *  palabras y se cambia con UNA acción, que escribe la etapa de siempre por la
 *  ruta de siempre: no hay ajuste nuevo que guardar. */
const ENDING_TITLE = '¿Cómo termina?'

/** LA TARJETA NO ES LA CONVERSACIÓN (F6-3). Juan, CP-5': "si tengo la etapa de
 *  traspaso invariablemente siempre se termina ahí? por ejemplo, si tengo etapa
 *  final atendido, perdido, pero aparte agrego traspaso, ahí qué sucede?". No
 *  sabía que las etapas finales del tablero cierran la TARJETA mientras el
 *  traspaso entrega la CONVERSACIÓN: conviven. Este bloque habla de la
 *  conversación, así que la línea vale para los tres terminales (pasar / cierre
 *  / fin_ok) y se dice como contexto de todo el bloque, no atada a un final. */
const ENDING_VS_CARD =
  'Esto es lo que pasa con la CONVERSACIÓN cuando el agente termina de capturar. Es distinto de las ' +
  'etapas finales del tablero (como Ganada o Perdida), donde queda la TARJETA cuando tu equipo ' +
  'cierra el caso.'

/** La frase del final, según lo que las etapas dicen hoy. El nombre de la etapa
 *  va dentro a propósito: es el lugar donde se cambia. */
function endingLine(terminal: AgentTerminal, handoffStageName: string): string {
  if (terminal === 'pasar') {
    return (
      'Cuando el agente termina de capturar, pasa la conversación con una persona. Lo decide la ' +
      `etapa "${handoffStageName}", que atiende tu equipo.`
    )
  }
  if (terminal === 'cierre') {
    return 'Cuando el agente termina de capturar, se despide y cierra la conversación.'
  }
  return 'Cuando el agente termina de capturar, se queda contestando lo que la persona pregunte.'
}

/** EL PROCESO DONDE EL AGENTE NO CAPTURA NADA (F4-10). Sin ninguna columna que
 *  compile no existe "cuando el agente termina de capturar": la frase derivada
 *  describía un final que nadie alcanza, y el botón habría creado la etapa de
 *  traspaso como PRIMERA columna del tablero (la posición calculada es 0). Se
 *  dice el hecho y no se ofrece nada. */
const ENDING_NO_CAPTURE =
  'Este proceso no tiene etapas donde el agente capture, así que no hay un final del agente que ' +
  'configurar.'

/** LA ETAPA DE TRASPASO QUE QUEDÓ DESPUÉS DEL FINAL (F4-10). El agente lee su
 *  terminal en la primera columna final, así que un traspaso colocado después
 *  nunca se alcanza. Aquí se decía "Agregar etapa de traspaso" y crearla
 *  contestaba 409 por una columna que el cliente nunca vio nombrada: lo que hay
 *  que hacer es moverla, y eso es lo que la frase pide. */
function strandedHandoffLine(stageName: string): string {
  return (
    `La etapa "${stageName}" está después de las etapas finales, así que el agente no la ` +
    'alcanza. Muévela antes de las etapas ganadas o perdidas para que el traspaso ocurra.'
  )
}

/** Con `fin_ok` no hay columna final que quitar ni traspaso que agregar sin
 *  esconder que este proceso todavía no tiene ninguna: el renglón dice dónde se
 *  cambia, que hasta F4-10 vivía sólo como comentario del código. */
const ENDING_CHANGED_IN_STAGES = 'Se cambia con las etapas del tablero.'

/** El aviso de una etapa de la VENTANA de captura que ya atiende una persona:
 *  la conversación se va a manos humanas ANTES de que la captura termine, así
 *  que el final de arriba no es lo que esa persona va a vivir. Es el mismo hecho
 *  que advierte la vista previa del agente, dicho con las mismas palabras. */
function midWindowWarning(stageName: string, owner: string): string {
  return (
    `Ojo: la etapa "${stageName}" la atiende ${owner}, así que la conversación pasa a manos ` +
    'humanas al entrar ahí, antes de que termine la captura.'
  )
}

/** Cómo se llama la etapa de traspaso que crea el botón. Un nombre propuesto, no
 *  impuesto: el diálogo lo deja editar, porque la columna es del cliente y va a
 *  vivir en su tablero. */
const HANDOFF_STAGE_DEFAULT_NAME = 'Traspaso'
/** El tope de la columna `pipeline_fields.question_prompt`. Se pone en el input
 *  para que no se pueda teclear de más, y el servidor lo vuelve a decir en
 *  palabras por si la petición no viene de esta pantalla. */
const QUESTION_PROMPT_MAX = 300
/** Qué pasa si lo dejas vacío, con la frase EXACTA que saldría. Se arma con la
 *  misma función que genera el flujo, así que la ayuda no puede prometer una
 *  pregunta que el agente no hace. */
function fichaPromptHelper(f: Pick<PipelineFieldView, 'key' | 'label' | 'dataType'>): string {
  return `Si lo dejas vacío, el agente pregunta: "${promptForField(f)}".`
}
/** EL NOMBRE ES UN DATO COMO LOS DEMÁS (F5-1). Juan, CP-4': "debemos de poder
 *  hacer lo mismo con nombre que con otros datos… si quieres que sea algo
 *  especial puede ser solo un atributo extra de los datos de un proceso".
 *
 *  F4-7 lo había dibujado como un pseudo-renglón propio, arriba de la lista, con
 *  su propio "Quitar", su propio diálogo y, cuando estaba quitado, un fantasma
 *  para devolverlo. Con el usuario real ese fantasma no se encontró: Juan
 *  intentó CREAR un dato llamado "nombre", es decir, buscó el camino normal. Así
 *  que el renglón especial se fue entero y el nombre se dibuja por el camino de
 *  cualquier dato — sus flechas de orden, su "Quitar", su frase, su menú de
 *  biblioteca — y se vuelve a agregar por el picker de siempre.
 *
 *  LO ÚNICO QUE LO DISTINGUE es esta línea en su meta, dicha donde se lee el
 *  resto de lo que el dato es. */
const NAME_KEY = 'nombre'
const NAME_ROW_HINT = 'Nombra la tarjeta del contacto.'

/** El estado vacío real de un proceso recién creado: no hereda la biblioteca. */
const FICHA_EMPTY =
  'Este proceso todavía no lleva datos. Agrega los que tu equipo necesita capturar aquí.'
/** Dónde viven las DEFINICIONES, dicho en el picker: crear un dato aquí lo
 *  agrega a la biblioteca del espacio, no solo a este proceso. */
const FICHA_LIBRARY_HINT =
  'Los datos se definen una vez para todo el espacio y cada proceso elige cuáles lleva. Los que crees aquí quedan disponibles para los demás procesos.'
/** De dónde sale el flujo del agente, dicho en Procesos (F2-6b D). Es el espejo
 *  exacto de la frase que /asistente ya trae: las dos pantallas se explican una
 *  a la otra, y ninguna de las dos deja al cliente adivinando dónde sigue. */
const PIPELINES_MIRROR =
  'Aquí modelas tus procesos. Cada canal lo atiende un agente y de ahí sale su flujo.'
/** Cuando un dato de la ficha lo llevan OTROS procesos (D5). Se dice en la fila
 *  y en el picker: editarlo o quitarlo toca a todos, y esa consecuencia no se
 *  puede pedir de memoria. */
const USED_IN_PREFIX = 'También se usa en: '
/** Los dos casos en que no hay nadie más. Se dicen distinto porque son hechos
 *  distintos: uno ya está en esta ficha, el otro todavía no está en ninguna. */
const USED_ONLY_HERE = 'Sólo se usa en este proceso.'
const USED_NOWHERE = 'Todavía no lo lleva ningún proceso.'

function DotsIcon(): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden className="size-4">
      <circle cx="12" cy="5" r="1.6" />
      <circle cx="12" cy="12" r="1.6" />
      <circle cx="12" cy="19" r="1.6" />
    </svg>
  )
}

/** La clave del dato de INTERÉS del giro (Especialidad, Modelo de interés,
 *  Artículo). Su valor vive en una columna del prospecto, no en la bolsa de
 *  datos: el área del tablero, el chip de la tarjeta y los filtros la leen. Por
 *  eso no se puede quitar de la ficha de un proceso, y el API dice lo mismo. */
const INTEREST_KEY = 'interes'
/** Por qué su fila no ofrece "Quitar". Se dice donde se buscaría el botón. */
const INTEREST_FIXED_HINT =
  'El interés es parte de la plataforma: impulsa el área del tablero y sus filtros.'

/** Las opciones de una lista: una por línea, o separadas por comas. La misma
 *  lectura que Personas (FieldsSection), para que la misma pantalla no se
 *  escriba de dos maneras según por dónde se entre. */
function parseFieldOptions(raw: string): string[] {
  return raw
    .split(/[\n,]/)
    .map((o) => o.trim())
    .filter((o) => o !== '')
}

/** Quién ve los prospectos de ESTE proceso (F18). La consecuencia de cada
 *  opción se lee bajo el Select, igual que la fuente de asignación del equipo:
 *  la visibilidad es per-proceso, así que se decide aquí y no en Operación
 *  (donde queda solo la de conversaciones). */
const VISIBILITY_LABEL: Record<PipelineVisibilityPolicy, string> = {
  all: 'Todo el equipo',
  assigned: 'Cada quien lo suyo',
}
const VISIBILITY_HELP: Record<PipelineVisibilityPolicy, string> = {
  all: 'La asignación es un filtro, no un muro.',
  assigned:
    'Cada asesor ve solo sus prospectos y los no asignados. Dueño y Administrador siempre ven todo.',
}

export function PipelinesSection(): JSX.Element {
  const qc = useQueryClient()
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['settings-pipelines'],
    queryFn: getPipelines,
  })
  // La BIBLIOTECA de datos de prospecto (F19): alimenta el selector del picker,
  // las etiquetas de "Requiere:" y "Obtiene:" en cada fila, y desde CP3 L las
  // acciones de biblioteca del menú de cada dato. La misma queryKey que lee el
  // selector de reglas de Operación, así comparten caché.
  //
  // Su ESTADO se mira, no se ignora: mientras carga, la sección de datos no
  // puede saber cuál está oculto ni qué acciones ofrece cada fila, y dibujarla
  // igual la hacía mentir por un instante (todo activo, menús vacíos). Si falla,
  // se dice y se ofrece reintentar en lugar de fingir una biblioteca vacía.
  const {
    data: leadFields,
    isLoading: fieldsLoading,
    isError: fieldsError,
    refetch: refetchFields,
  } = useQuery({
    queryKey: ['fields', 'lead'],
    queryFn: () => listFields('lead'),
  })
  // Los agentes del espacio: el flujo de captura se publica EN uno de ellos, y
  // la pantalla de Procesos no conoce ninguno por sí sola.
  const { data: workers } = useQuery({ queryKey: ['workers'], queryFn: getWorkers })
  const agents = workers ?? []
  const libraryReady = !fieldsLoading && !fieldsError && leadFields !== undefined
  const activeLeadFields = (leadFields ?? []).filter((f) => f.active)
  const fieldLabelOf = new Map(activeLeadFields.map((f) => [f.key, f.label]))

  const [selectedId, setSelectedId] = useState('')
  const [busy, setBusy] = useState(false)
  // Create / rename / delete a funnel.
  const [createOpen, setCreateOpen] = useState(false)
  const [newName, setNewName] = useState('')
  const [renameOpen, setRenameOpen] = useState(false)
  const [renameValue, setRenameValue] = useState('')
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [deleteConfirm, setDeleteConfirm] = useState('')
  // La ficha del proceso (CP3 K): el picker para agregar, el formulario para
  // crear uno nuevo dentro del picker, y el dato que se está por quitar.
  const [fieldPickerOpen, setFieldPickerOpen] = useState(false)
  const [fieldCreating, setFieldCreating] = useState(false)
  const [newFieldLabel, setNewFieldLabel] = useState('')
  const [newFieldType, setNewFieldType] = useState<FieldDataType>('text')
  const [newFieldOptions, setNewFieldOptions] = useState('')
  // De dónde saldrán sus opciones (D21-1): 'lista' es lo de siempre.
  const [newFieldSource, setNewFieldSource] = useState<FieldOptionsSource>('lista')
  // El dato que YA nació en la biblioteca y todavía no entró a la ficha. Crear
  // desde aquí son dos escrituras, y cuando la segunda falla la primera ya
  // ocurrió: sin recordarla, reintentar volvía a crear el mismo dato y el API
  // contestaba "Ya existe un campo con esa clave", que es la peor manera de
  // enterarse de que el primer intento sí había funcionado.
  const [createdPending, setCreatedPending] = useState<FieldDefinition | null>(null)
  const [fieldRemoveTarget, setFieldRemoveTarget] = useState<PipelineFieldView | null>(null)
  // Las filas de la ficha con el detalle ABIERTO (F2-6b B). La lista era un
  // recordatorio: el nombre, el tipo y una cuenta de opciones que había que
  // creerse de memoria. El detalle enseña lo guardado (las opciones enteras y
  // en qué otros procesos vive el dato) sin salir de la fila ni abrir un
  // segundo editor: editar sigue estando en el menú, donde siempre estuvo.
  const [openDetails, setOpenDetails] = useState<string[]>([])
  // Las acciones de BIBLIOTECA sobre un dato de la ficha (CP3 L): editar su
  // nombre y sus opciones, ocultarlo del catálogo, eliminarlo. Vivían en la
  // pestaña Campos; al mudarse la mitad de prospectos aquí, se mudaron con ella
  // para que nada de lo que se podía hacer dejara de poderse.
  const [fieldEditTarget, setFieldEditTarget] = useState<FieldDefinition | null>(null)
  const [editFieldLabel, setEditFieldLabel] = useState('')
  const [editFieldOptions, setEditFieldOptions] = useState('')
  const [editFieldSource, setEditFieldSource] = useState<FieldOptionsSource>('lista')
  const [fieldDeleteTarget, setFieldDeleteTarget] = useState<FieldDefinition | null>(null)
  // La FRASE de cada dato mientras se está escribiendo (F4-4), por id. Vive
  // aparte de la ficha del servidor porque un campo de texto no puede guardar en
  // cada tecla: lo escrito se queda aquí hasta que la fila lo guarda, y lo que
  // no se tocó se lee de la ficha.
  const [promptDrafts, setPromptDrafts] = useState<Record<string, string>>({})
  /** Lo que se está escribiendo en "Cómo lo reconoce el agente", por proceso.
   *  Ausente = no se ha tocado, y entonces vale lo guardado. Por proceso y no
   *  uno solo porque cambiar de proceso en la lista no puede llevarse el
   *  borrador del anterior al campo del siguiente. */
  const [entryLabelDrafts, setEntryLabelDrafts] = useState<Record<string, string>>({})
  /** Lo que se está escribiendo en "Qué significa este proceso" (F5-2), por
   *  proceso. Misma disciplina que el borrador de la etiqueta, y por la misma
   *  razón: un campo de texto no guarda en cada tecla, y cambiar de proceso no
   *  puede llevarse el borrador del anterior al campo del siguiente. */
  const [meaningDrafts, setMeaningDrafts] = useState<Record<string, string>>({})

  /** El probador de ruteo (W4'-7): la frase escrita, la oración del servidor (o
   *  la queja fija) y si hay una llamada en vuelo. Vive junto a los borradores
   *  de la puerta porque es su comprobación. */
  const [testerText, setTesterText] = useState('')
  const [testerResult, setTesterResult] = useState<string | null>(null)
  const [testerBusy, setTesterBusy] = useState(false)
  // ¿Cómo termina? (F4-6): el diálogo que agrega la etapa de traspaso, con su
  // nombre propuesto, y la confirmación de quitarla. Quitar una etapa es
  // destructivo y se pregunta antes, como todo lo demás en esta pantalla.
  const [endingAddOpen, setEndingAddOpen] = useState(false)
  const [endingStageName, setEndingStageName] = useState(HANDOFF_STAGE_DEFAULT_NAME)
  const [endingRemoveTarget, setEndingRemoveTarget] = useState<PipelineStageView | null>(null)

  // Follow the server: after a delete the selected funnel is gone, and after a
  // create the tenant means the one they just made.
  const pipelines = data?.pipelines ?? []
  useEffect(() => {
    if (pipelines.length === 0) return
    if (pipelines.some((p) => p.id === selectedId)) return
    setSelectedId(pipelines.find((p) => p.isDefault)?.id ?? pipelines[0]!.id)
  }, [pipelines, selectedId])

  if (isError) {
    return <QueryError message="No pudimos cargar tus procesos." onRetry={() => void refetch()} />
  }
  if (isLoading || !data) return <Spinner label="Cargando procesos…" />

  const members = data.members
  const selected: PipelineView | undefined =
    pipelines.find((p) => p.id === selectedId) ?? pipelines[0]
  const stages = selected?.stages ?? []
  // La marca solo vive en etapas abiertas (la base de datos la rechaza en las
  // demás), así que el Select ni siquiera ofrece las otras.
  const openStages = stages.filter((s) => s.kind === 'open')
  const commitmentStage = stages.find((s) => s.isCommitment)
  // La etapa que AGENDA, si el proceso tiene una (D22-9). Hay a lo más una: la
  // base la pega a la marca de cita agendada, y de esa hay una por proceso.
  const agendaStage = stages.find((s) => s.behavior === 'agenda')
  const deleteBlocker = selected ? pipelineDeleteBlocker(selected, pipelines.length) : null
  // La FICHA del proceso seleccionado, y lo que la BIBLIOTECA todavía puede
  // aportarle: los datos activos del espacio que este proceso aún no lleva.
  //
  // La ficha llega con sus datos OCULTOS incluidos (el API se los manda solo a
  // esta pantalla): ocultar un dato no le quita su lugar, y esta es la única
  // pantalla donde se puede volver a activar. Las etapas, en cambio, solo
  // pueden exigir u obtener datos ACTIVOS, así que sus selectores leen
  // `fichaActive` y no `ficha`: ofrecer uno oculto sería una casilla que el
  // servidor rechaza al guardar.
  const ficha = selected?.fields ?? []
  const fichaIds = ficha.map((f) => f.id)
  const hiddenFieldIds = new Set((leadFields ?? []).filter((f) => !f.active).map((f) => f.id))
  const fichaActive = ficha.filter((f) => !hiddenFieldIds.has(f.id))
  const libraryAvailable = activeLeadFields.filter((f) => !fichaIds.includes(f.id))
  // Las etapas que perderían el dato si se quita: las que lo exigen y las que
  // lo obtienen (la captura compilada pregunta por lo que la etapa obtiene), la
  // misma cuenta que hace el API al recortar, dicha ANTES en el diálogo.
  const fieldRemoveLosers = fieldRemoveTarget ? stagesRequiring(stages, fieldRemoveTarget.key) : []
  // CÓMO TERMINA ESTE PROCESO (F4-6), derivado de sus etapas con la MISMA cuenta
  // que compila el agente. No se guarda en ningún lado: se lee.
  //
  // Con la ficha ACTIVA (F4-10). El servidor planea sobre los datos activos, así
  // que pasarle los ocultos hacía que la ventana de captura de esta pantalla
  // fuera MÁS LARGA que la del agente: la posición de inserción y la etapa de la
  // advertencia podían nombrar una columna a la que el agente nunca llega.
  const ending = readPipelineEnding(stages, fichaActive)
  /** Quién atiende una etapa, en las palabras de la frase del aviso. */
  const ownerWordsOf = (s: PipelineStageView): string =>
    s.ownerType === 'team'
      ? 'tu equipo'
      : (members.find((m) => m.id === s.ownerMemberId)?.name ?? 'una persona')

  /** Run a mutation, seed the cache with the board it returned and toast. The
   *  API's own sentence wins on failure: every refusal here already names the
   *  rule that stopped you, in Spanish. Returns the new board so a caller can
   *  point the screen at something that did not exist a moment ago. */
  async function run(
    fn: () => Promise<Awaited<ReturnType<typeof getPipelines>>>,
    okMsg: string,
  ): Promise<Awaited<ReturnType<typeof getPipelines>> | null> {
    setBusy(true)
    try {
      const next = await fn()
      qc.setQueryData(['settings-pipelines'], next)
      // The board and the flow editor's pickers read the same funnel.
      await qc.invalidateQueries({ queryKey: ['leads'] })
      // Y "Así quedaría tu agente" se relee sola: por eso no tiene botón. La
      // puerta del Publicar describe el mismo agente, así que se relee con ella
      // o el botón sigue diciendo "ya refleja tus procesos" sobre un agente que
      // acaba de cambiar.
      void qc.invalidateQueries({ queryKey: ['agent-compiled-preview'] })
      void qc.invalidateQueries({ queryKey: ['agent-publish-preview'] })
      toast.ok(okMsg)
      return next
    } catch (err) {
      toast.error(apiErrorMessage(err, 'No pudimos guardar el cambio.'))
      return null
    } finally {
      setBusy(false)
    }
  }

  async function submitCreate(): Promise<void> {
    const name = newName.trim()
    if (name === '') {
      toast.error('Escribe un nombre para el proceso.')
      return
    }
    const known = new Set(pipelines.map((p) => p.id))
    const next = await run(() => createPipeline(name), 'Proceso creado con sus etapas iniciales.')
    if (!next) return
    // You made it to look at it: select it. The id is whatever the server
    // slugged, so it is found by "the one that was not there before".
    const created = next.pipelines.find((p) => !known.has(p.id))
    if (created) setSelectedId(created.id)
    setCreateOpen(false)
    setNewName('')
  }

  async function submitRename(): Promise<void> {
    const name = renameValue.trim()
    if (!selected || name === '') return
    if (
      await run(() => updatePipeline(selected.id, { name }), 'Guardamos el nombre del proceso.')
    ) {
      setRenameOpen(false)
    }
  }

  /** Lo que hay HOY en el campo de "Cómo lo reconoce el agente": lo que se está
   *  escribiendo si hay algo, y si no lo guardado. Vacío significa "usa la
   *  derivada del nombre", que es exactamente lo que el placeholder enseña. */
  function entryLabelValueOf(p: PipelineView): string {
    return entryLabelDrafts[p.id] ?? p.entryLabel ?? ''
  }

  /** Guardar la frase con la que el agente reconoce este proceso. Sólo se
   *  dispara cuando de verdad cambió: salir de un campo que no se tocó no puede
   *  publicar una versión gemela (misma disciplina que la frase de un dato).
   *
   *  En blanco no es un hueco: es "vuelve a derivarla del nombre", así que se
   *  manda vacía en vez de omitirse, y el toast lo dice con esas palabras. Las
   *  quejas del servidor (choque con otra opción del menú, largo) suben tal cual
   *  por `run`: ahí está dicho con qué chocó. */
  async function saveEntryLabel(p: PipelineView): Promise<void> {
    const draft = entryLabelDrafts[p.id]
    if (draft === undefined) return
    const next = draft.trim()
    const dropDraft = (): void =>
      setEntryLabelDrafts((prev) => {
        const { [p.id]: _drop, ...rest } = prev
        return rest
      })
    if (next === (p.entryLabel ?? '')) {
      dropDraft()
      return
    }
    const ok = await run(
      () => updatePipeline(p.id, { entryLabel: next }),
      next === ''
        ? `"${p.name}" vuelve a reconocerse con la frase de siempre.`
        : `Cambiamos cómo el agente reconoce "${p.name}".`,
    )
    // Lo guardado es la verdad. Si falló, el borrador se queda para que se pueda
    // corregir la frase que el servidor acaba de rechazar sin volver a teclearla.
    if (ok) dropDraft()
  }

  /** Lo que hay HOY en el campo del significado: el borrador si se está
   *  escribiendo, y si no lo guardado. Vacío significa "reconócelo por su
   *  etiqueta", que es lo que el agente hacía antes de F5-2. */
  function meaningValueOf(p: PipelineView): string {
    return meaningDrafts[p.id] ?? p.meaning ?? ''
  }

  /** Guardar el significado del proceso (F5-2). Copia exacta de la disciplina de
   *  la etiqueta: sólo guarda cuando de verdad cambió, en blanco se manda vacío
   *  (es "bórralo", no un hueco) y las quejas del servidor — el largo, sobre
   *  todo — suben tal cual, que es donde está dicho qué acortar. */
  async function saveMeaning(p: PipelineView): Promise<void> {
    const draft = meaningDrafts[p.id]
    if (draft === undefined) return
    const next = draft.trim()
    const dropDraft = (): void =>
      setMeaningDrafts((prev) => {
        const { [p.id]: _drop, ...rest } = prev
        return rest
      })
    if (next === (p.meaning ?? '')) {
      dropDraft()
      return
    }
    const ok = await run(
      () => updatePipeline(p.id, { meaning: next }),
      next === ''
        ? `"${p.name}" vuelve a reconocerse solo por su frase de entrada.`
        : `Guardamos qué significa "${p.name}".`,
    )
    if (ok) dropDraft()
  }

  /** Probar una frase contra el clasificador real (W4'-7). Cuesta una llamada
   *  al LLM por Probar, así que sólo dispara el botón, nunca el tecleo. El
   *  resultado es la oración del SERVIDOR verbatim; cualquier fallo es la misma
   *  queja fija, porque aquí no hay nada que el cliente pueda corregir de ella. */
  async function probarFrase(): Promise<void> {
    const phrase = testerText.trim()
    const agentId = agents[0]?.id ?? ''
    if (phrase === '' || agentId === '' || testerBusy) return
    setTesterBusy(true)
    try {
      setTesterResult((await routeTest(agentId, phrase)).sentence)
    } catch {
      setTesterResult(TESTER_ERROR)
    } finally {
      setTesterBusy(false)
    }
  }

  async function makeDefault(): Promise<void> {
    if (!selected) return
    await run(
      () => updatePipeline(selected.id, { isDefault: true }),
      'Este proceso es ahora el predeterminado.',
    )
  }

  async function submitDelete(): Promise<void> {
    if (!selected) return
    if (await run(() => deletePipeline(selected.id), 'Proceso eliminado.')) {
      setDeleteOpen(false)
      setDeleteConfirm('')
    }
  }

  // ─── la ficha del proceso (CP3 K) ──────────────────────────────────────────

  /** Lo que hay HOY en el campo de una fila: lo que se está escribiendo si hay
   *  algo, y si no lo guardado. Vacío significa "usa la frase generada", que es
   *  exactamente lo que el placeholder enseña. */
  function promptValueOf(f: PipelineFieldView): string {
    return promptDrafts[f.id] ?? f.questionPrompt ?? ''
  }

  /** La ficha como viaja al guardar: el orden pedido, y la frase de cada dato.
   *
   *  TODO guardado la manda entera, incluso el que sólo reordena: el PUT
   *  reemplaza la ficha completa, así que una lista sin frases sería una lista
   *  que las borra todas. */
  function fichaSave(ids: string[]): PipelineFieldSave[] {
    const byId = new Map(ficha.map((f) => [f.id, f]))
    return ids.map((id) => {
      const stored = byId.get(id)
      const prompt = (promptDrafts[id] ?? stored?.questionPrompt ?? '').trim()
      return prompt === '' ? { id } : { id, questionPrompt: prompt }
    })
  }

  /** Guardar la frase de UN dato. Es el mismo guardado de ficha de siempre — la
   *  lista entera, en su orden — porque la frase vive en el renglón de la
   *  ficha. Sólo se dispara cuando de verdad cambió: salir de un campo que no
   *  se tocó no puede publicar una versión gemela. */
  async function savePrompt(f: PipelineFieldView): Promise<void> {
    const draft = promptDrafts[f.id]
    if (draft === undefined) return
    const next = draft.trim()
    if (next === (f.questionPrompt ?? '')) {
      setPromptDrafts((prev) => {
        const { [f.id]: _drop, ...rest } = prev
        return rest
      })
      return
    }
    await saveFicha(
      fichaIds,
      next === ''
        ? `"${f.label}" vuelve a preguntarse con la frase de siempre.`
        : `Cambiamos cómo el agente pregunta "${f.label}".`,
    )
  }

  /** Guarda la ficha completa, en orden, y CUENTA lo que pasó — pero deja subir
   *  el fallo. Existe aparte de `saveFicha` porque crear un dato son dos
   *  escrituras, y solo quien hizo las dos sabe cuál se cayó: si esta se
   *  quejara sola, un dato que ya nació en la biblioteca se anunciaría con un
   *  "no pudimos guardar el cambio" que suena a que no se creó nada.
   *
   *  La respuesta trae algo más que el tablero. Las etapas a las que el
   *  guardado les quitó la exigencia (advertirlo antes y no decir después lo
   *  que de verdad pasó serían dos mitades de la misma omisión), y el dato de
   *  interés que el servidor sentó por su cuenta: aparece en la ficha sin que
   *  nadie lo haya pedido, así que se dice, y se dice para qué sirve. */
  async function saveFichaOrThrow(
    pipelineId: string,
    fieldIds: string[],
    okMsg: string,
  ): Promise<void> {
    const next = await updatePipelineFields(pipelineId, fichaSave(fieldIds))
    // Lo guardado es la verdad: los borradores de frase dejan de existir, o el
    // siguiente guardado volvería a mandar lo que ya se mandó.
    setPromptDrafts({})
    qc.setQueryData(['settings-pipelines'], next)
    await qc.invalidateQueries({ queryKey: ['leads'] })
    // La vista previa del agente pregunta EXACTAMENTE estos datos, y la puerta
    // del Publicar compara contra ellos: se releen juntas.
    void qc.invalidateQueries({ queryKey: ['agent-compiled-preview'] })
    void qc.invalidateQueries({ queryKey: ['agent-publish-preview'] })
    const parts = [okMsg]
    if (next.stripped.length > 0) {
      parts.push(`${STRIPPED_LEAD} ${next.stripped.map((s) => s.name).join(', ')}.`)
    }
    if (next.seatedInterest !== undefined) {
      parts.push(
        `Agregamos "${next.seatedInterest}" porque es parte de la plataforma: impulsa el área del tablero y sus filtros.`,
      )
    }
    toast.ok(parts.join(' '))
  }

  /** El guardado de siempre: el mismo trabajo, con la queja puesta. La frase del
   *  API gana en el fallo, igual que en `run`. */
  async function saveFicha(fieldIds: string[], okMsg: string): Promise<boolean> {
    if (!selected) return false
    setBusy(true)
    try {
      await saveFichaOrThrow(selected.id, fieldIds, okMsg)
      return true
    } catch (err) {
      toast.error(apiErrorMessage(err, 'No pudimos guardar el cambio.'))
      return false
    } finally {
      setBusy(false)
    }
  }

  /** La biblioteca de prospectos cambió: el selector de reglas de Operación y
   *  esta misma ficha leen la misma queryKey. */
  async function refreshLibrary(): Promise<void> {
    await qc.invalidateQueries({ queryKey: ['fields', 'lead'] })
    // Cambiarle el tipo o las opciones a un dato cambia cómo el agente lee la
    // respuesta, que es lo que la vista previa dibuja y lo que la puerta del
    // Publicar compara.
    void qc.invalidateQueries({ queryKey: ['agent-compiled-preview'] })
    void qc.invalidateQueries({ queryKey: ['agent-publish-preview'] })
  }

  function closeFieldPicker(): void {
    setFieldPickerOpen(false)
    setFieldCreating(false)
    setNewFieldLabel('')
    setNewFieldType('text')
    setNewFieldOptions('')
    setNewFieldSource('lista')
    setCreatedPending(null)
  }

  /** Un dato nuevo que TOMARÁ sus opciones de una fuente (D21-1). Solo una
   *  lista puede tener fuente, así que la pregunta se va con el tipo. */
  const newFieldSourced = newFieldType === 'enum' && newFieldSource !== 'lista'

  /** Agregar un dato que ya existe en la biblioteca: va al final de la ficha. */
  async function addFieldToFicha(fieldId: string): Promise<void> {
    if (await saveFicha([...fichaIds, fieldId], 'Agregamos el dato al proceso.')) {
      closeFieldPicker()
    }
  }

  /** Crear un dato NUEVO desde el proceso. Son dos escrituras y se dicen como
   *  una sola: el dato nace en la biblioteca del espacio (POST /me/fields, el
   *  mismo endpoint que sirve a las personas) y acto seguido entra a la ficha de
   *  este proceso. Sin el segundo paso quedaría creado y en ningún lado. */
  async function submitFieldCreate(): Promise<void> {
    if (!selected) return
    const label = newFieldLabel.trim()
    if (label === '') {
      toast.error('Escribe un nombre para el dato.')
      return
    }
    const options = parseFieldOptions(newFieldOptions)
    if (newFieldType === 'enum' && !newFieldSourced && options.length === 0) {
      toast.error('Un dato de tipo lista necesita al menos una opción.')
      return
    }
    // Si el dato ya nació en un intento anterior, el reintento es SOLO el
    // segundo paso. Volver a crearlo devolvería un 409 por clave repetida, que
    // acusa de duplicar algo a quien nada más apretó el botón otra vez.
    let created = createdPending
    if (created === null) {
      setBusy(true)
      try {
        created = await createField({
          entity_type: 'lead',
          key: slugifyKey(label),
          label,
          data_type: newFieldType,
          // Con fuente no van opciones: la definición no guarda copia de una
          // lista viva, y mandarlas sería un 400 en palabras.
          ...(newFieldType === 'enum' && !newFieldSourced ? { options } : {}),
          ...(newFieldSourced ? { options_source: newFieldSource } : {}),
        })
      } catch (err) {
        toast.error(apiErrorMessage(err, 'No pudimos crear el dato.'))
        return
      } finally {
        setBusy(false)
      }
      setCreatedPending(created)
      await refreshLibrary()
    }
    // Dos escrituras, dos verdades. Si la ficha se cae, lo que pasó no es "no
    // pudimos guardar el cambio": el dato existe, y lo único que falta es
    // meterlo a este proceso. Decirlo con las palabras del guardado de ficha
    // haría creer que hay que volver a crearlo.
    setBusy(true)
    try {
      await saveFichaOrThrow(
        selected.id,
        [...fichaIds, created.id],
        'Creamos el dato y lo agregamos al proceso.',
      )
    } catch {
      toast.error(
        `Creamos el dato "${created.label}", pero no pudimos agregarlo al proceso. Vuelve a intentarlo.`,
      )
      return
    } finally {
      setBusy(false)
    }
    closeFieldPicker()
  }

  /** Quitar un dato de la ficha. El diálogo ya dijo qué etapas dejan de
   *  exigirlo; el API hace el recorte en la misma transacción. */
  async function submitFieldRemove(): Promise<void> {
    if (!fieldRemoveTarget) return
    if (
      await saveFicha(
        fichaIds.filter((id) => id !== fieldRemoveTarget.id),
        'Quitamos el dato del proceso.',
      )
    ) {
      setFieldRemoveTarget(null)
    }
  }

  /** Mover un dato un lugar, igual que las etapas: se manda la lista entera en
   *  su nuevo orden, no un intercambio. */
  async function moveField(field: PipelineFieldView, delta: number): Promise<void> {
    const index = fichaIds.indexOf(field.id)
    const next = index + delta
    if (index < 0 || next < 0 || next >= fichaIds.length) return
    const reordered = [...fichaIds]
    reordered.splice(index, 1)
    reordered.splice(next, 0, field.id)
    await saveFicha(reordered, 'Cambiamos el orden de los datos.')
  }

  /** La DEFINICIÓN de biblioteca detrás de un dato de la ficha. La ficha guarda
   *  la proyección (id, clave, etiqueta, tipo); `managed` y `active` viven en el
   *  catálogo, y son justo lo que decide qué acciones se ofrecen. */
  function libraryDefOf(f: PipelineFieldView): FieldDefinition | null {
    return (leadFields ?? []).find((d) => d.id === f.id) ?? null
  }

  function openFieldEdit(def: FieldDefinition): void {
    setFieldEditTarget(def)
    setEditFieldLabel(def.label)
    // Una por línea, igual que Personas: es la forma en que se vuelven a
    // escribir, así que es la forma en que se muestran. En un dato con fuente
    // la lista que llegó es la VIVA, y sirve de punto de partida si se decide
    // volver a escribirla a mano.
    setEditFieldOptions((def.options ?? []).join('\n'))
    setEditFieldSource(def.options_source)
  }

  /** El dato que se está editando toma hoy (en este diálogo) sus opciones de
   *  una fuente: la lista se enseña, no se escribe. */
  const editFieldSourced = fieldEditTarget?.data_type === 'enum' && editFieldSource !== 'lista'

  /** Guardar la edición de biblioteca. Un campo del sistema conserva su NOMBRE y
   *  su TIPO (los nombran los flujos, las reglas y los requisitos de etapa);
   *  mandar el label lo haría rebotar con 409. Sus OPCIONES sí son del espacio. */
  async function submitFieldEdit(): Promise<void> {
    if (!fieldEditTarget) return
    const options = parseFieldOptions(editFieldOptions)
    if (fieldEditTarget.data_type === 'enum' && !editFieldSourced && options.length === 0) {
      toast.error('Un dato de tipo lista necesita al menos una opción.')
      return
    }
    setBusy(true)
    try {
      await updateField(fieldEditTarget.id, {
        ...(fieldEditTarget.managed ? {} : { label: editFieldLabel.trim() }),
        // Con fuente no viajan opciones (el API contesta 409 y dice dónde se
        // cambian); sin fuente viajan siempre, incluso al volver de una, que es
        // justo cuando el API las exige.
        ...(fieldEditTarget.data_type === 'enum' && !editFieldSourced ? { options } : {}),
        ...(fieldEditTarget.data_type === 'enum' ? { options_source: editFieldSource } : {}),
      })
      await refreshLibrary()
      // La fila del dato vive en la ficha, que viene del tablero: el nombre y
      // la cuenta de opciones que se acaban de editar se leen de ahí, así que
      // sin volver a leerlo la edición no se veía hasta recargar la página.
      await qc.invalidateQueries({ queryKey: ['settings-pipelines'] })
      toast.ok('Dato actualizado.')
      setFieldEditTarget(null)
    } catch (err) {
      toast.error(apiErrorMessage(err, 'No pudimos actualizar el dato.'))
    } finally {
      setBusy(false)
    }
  }

  /** Ocultar / activar el dato en la BIBLIOTECA. Ocultarlo no lo quita de la
   *  ficha de ningún proceso: deja de ofrecerse para reglas y datos nuevos. */
  async function toggleFieldActive(def: FieldDefinition): Promise<void> {
    setBusy(true)
    try {
      const { strippedStages: stripped } = await updateField(def.id, { active: !def.active })
      await refreshLibrary()
      // La fila del dato vive en la ficha, que viene del tablero: sin volver a
      // leerlo, ocultar y activar no dejaban rastro en la pantalla donde se
      // hicieron, y "Oculto en el catálogo" solo aparecía tras recargar.
      await qc.invalidateQueries({ queryKey: ['settings-pipelines'] })
      toast.ok(
        def.active
          ? stripped.length > 0
            ? `Dato oculto. ${STRIPPED_LEAD} ${stripped.map((s) => `${s.name} (${s.pipelineName})`).join(', ')}.`
            : 'Dato oculto.'
          : 'Dato activado.',
      )
    } catch (err) {
      toast.error(apiErrorMessage(err, 'No pudimos cambiar el dato.'))
    } finally {
      setBusy(false)
    }
  }

  /** Eliminar el dato de la biblioteca ENTERA. El API se niega cuando está en
   *  uso y su negativa es lo único que dice qué hacer después, así que se cita. */
  async function submitFieldDelete(): Promise<void> {
    if (!fieldDeleteTarget) return
    setBusy(true)
    try {
      const { strippedStages: stripped } = await deleteField(fieldDeleteTarget.id)
      await refreshLibrary()
      // El dato desapareció del catálogo: la ficha de cada proceso lo pierde
      // también, así que la lista de procesos se vuelve a leer.
      await qc.invalidateQueries({ queryKey: ['settings-pipelines'] })
      // Igual que al ocultar: si el borrado destrabó etapas, se dice cuáles.
      toast.ok(
        stripped.length > 0
          ? `Dato eliminado. ${STRIPPED_LEAD} ${stripped.map((s) => `${s.name} (${s.pipelineName})`).join(', ')}.`
          : 'Dato eliminado.',
      )
      setFieldDeleteTarget(null)
    } catch (err) {
      toast.error(apiErrorMessage(err, 'No pudimos eliminar el dato.'))
    } finally {
      setBusy(false)
    }
  }

  /** Las acciones de biblioteca que ofrece UN dato de la ficha, con las mismas
   *  guardas que tenía la pestaña Campos: un campo del sistema no se renombra
   *  ni se elimina, pero su lista sí se edita. */
  function fieldMenuFor(f: PipelineFieldView): MenuItem[] {
    const def = libraryDefOf(f)
    if (!def) return []
    const items: MenuItem[] = []
    if (!def.managed) items.push({ label: 'Editar dato', onSelect: () => openFieldEdit(def) })
    else if (def.data_type === 'enum')
      items.push({ label: 'Editar opciones', onSelect: () => openFieldEdit(def) })
    items.push({
      label: def.active ? 'Ocultar del catálogo' : 'Activar en el catálogo',
      onSelect: () => void toggleFieldActive(def),
    })
    if (!def.managed)
      items.push({
        label: 'Eliminar del catálogo',
        danger: true,
        onSelect: () => setFieldDeleteTarget(def),
      })
    return items
  }

  /** El tipo del dato en una línea. Una lista dice CUÁNTAS opciones tiene:
   *  "Lista de opciones" sin el número no distingue una lista de dos de una de
   *  veinte, que es justo lo que hace falta saber al mirarla. */
  function fieldMeta(f: PipelineFieldView): string {
    const parts: string[] = [DATA_TYPE_LABELS[f.dataType]]
    if (f.dataType === 'enum') {
      const n = f.options?.length ?? 0
      parts.push(`${n} ${n === 1 ? 'opción' : 'opciones'}`)
    }
    // Ocultar un dato del catálogo tiene que VERSE donde se ocultó, o la acción
    // no deja rastro y parece que no hizo nada.
    const def = libraryDefOf(f)
    if (def && !def.active) parts.push('Oculto en el catálogo')
    // El interés no se puede quitar, y el lugar donde se buscaría el botón es
    // el lugar donde hay que decir por qué no está.
    if (f.key === INTEREST_KEY) parts.push(INTEREST_FIXED_HINT)
    // Lo único especial que le queda al nombre (F5-1): nombra la tarjeta. Va en
    // la meta, junto al resto de lo que el dato es, y no como un bloque aparte
    // — un bloque aparte es exactamente de lo que se le acaba de sacar.
    if (f.key === NAME_KEY) parts.push(NAME_ROW_HINT)
    return parts.join(' · ')
  }

  function toggleDetail(key: string): void {
    setOpenDetails((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]))
  }

  /** Los OTROS procesos que llevan este mismo dato. Sale del tablero que la
   *  pantalla ya tiene: cada proceso viene con su ficha, así que la respuesta
   *  está en la caché y no hace falta preguntarle nada más al servidor.
   *  `exceptId` es el proceso desde el que se mira (vacío en el picker, donde
   *  el dato todavía no está en ninguna ficha de aquí). */
  function usedInOther(fieldId: string, exceptId: string): string[] {
    return pipelines
      .filter((p) => p.id !== exceptId && p.fields.some((x) => x.id === fieldId))
      .map((p) => p.name)
  }

  /** La misma frase en los dos lugares donde se decide sobre un dato: la fila
   *  de la ficha y la fila del picker. Quitar o editar un dato compartido toca
   *  a todos los procesos que lo llevan, y eso se lee ANTES de decidir. */
  function usedInLine(fieldId: string, exceptId: string): string {
    const others = usedInOther(fieldId, exceptId)
    if (others.length > 0) return `${USED_IN_PREFIX}${others.join(', ')}.`
    return exceptId === '' ? USED_NOWHERE : USED_ONLY_HERE
  }

  /** Lo mismo, para un dato de la BIBLIOTECA que todavía no está en la ficha:
   *  en el picker se elige a ciegas si la fila solo trae el nombre, y una lista
   *  sin su cuenta de opciones no se distingue de otra. Sin "Oculto en el
   *  catálogo" ni el interés, que son hechos de la ficha y no del picker (que
   *  solo ofrece datos activos que este proceso aún no lleva). */
  function libraryFieldMeta(f: FieldDefinition): string {
    const parts: string[] = [DATA_TYPE_LABELS[f.data_type]]
    if (f.data_type === 'enum') {
      const n = f.options?.length ?? 0
      parts.push(`${n} ${n === 1 ? 'opción' : 'opciones'}`)
    }
    return parts.join(' · ')
  }

  /** Mover una marca es encenderla en su nueva etapa: el servidor apaga a la
   *  anterior en la misma transacción, así que el proceso nunca tiene dos. Va
   *  por el mismo PATCH de siempre; lo que cambió es dónde se elige. */
  async function moveMarker(stageId: string, edit: StageEdit, okMsg: string): Promise<void> {
    if (!selected) return
    await run(() => updateStage(selected.id, stageId, edit), okMsg)
  }

  /** El muro de visibilidad de ESTE proceso. Guarda por el PATCH de proceso de
   *  siempre; el tablero y los prospectos lo leen del proceso, no del espacio. */
  async function setVisibility(policy: PipelineVisibilityPolicy): Promise<void> {
    if (!selected) return
    await run(
      () => updatePipeline(selected.id, { visibilityPolicy: policy }),
      'Guardamos quién ve los prospectos de este proceso.',
    )
  }

  /** "Ninguna" apaga la marca en quien la tenga: el proceso queda sin etapa de
   *  cita agendada, agendar no mueve la tarjeta y el KPI de citas se oculta. */
  async function setCommitment(stageId: string): Promise<void> {
    if (stageId === NO_STAGE) {
      const holder = stages.find((s) => s.isCommitment)
      if (holder) {
        await moveMarker(
          holder.id,
          { isCommitment: false },
          'Este proceso ya no tiene etapa de cita agendada.',
        )
      }
      return
    }
    await moveMarker(stageId, { isCommitment: true }, 'Actualizamos la etapa de cita agendada.')
  }

  /** Agregar la etapa de traspaso, EN SU LUGAR: justo después de la última
   *  columna que la captura recorre. Creada al final quedaría después de Ganada
   *  y Perdida, donde la conversación ya terminó: el final seguiría diciendo lo
   *  mismo y el botón sería una puerta pintada. */
  async function submitEndingAdd(): Promise<void> {
    if (!selected) return
    const name = endingStageName.trim()
    if (name === '') {
      toast.error('Escribe un nombre para la etapa.')
      return
    }
    const next = await run(
      () =>
        createStage(selected.id, {
          name,
          kind: 'open',
          behavior: 'handoff',
          // NACE DEL EQUIPO (F5-3). Juan, punto 5 de CP-4': "se crea con
          // responsable el agente aunque según se manda a una persona". Toda la
          // razón de ser de esta etapa es entregar la conversación a una
          // persona, así que nacida del Agente la fila se contradecía sola
          // ("Pasa con una persona" arriba, "Responsable: El Agente" abajo) y la
          // frase del final prometía una etapa "que atiende tu equipo" que
          // nadie del equipo atendía. Sin política: el equipo la recibe sin
          // asignación automática, y quien quiera repartirla lo elige en la
          // etapa.
          ownerType: 'team',
          position: ending.insertPosition,
        }),
      'Al terminar de capturar, el agente pasa la conversación con una persona.',
    )
    if (next) {
      setEndingAddOpen(false)
      setEndingStageName(HANDOFF_STAGE_DEFAULT_NAME)
    }
  }

  /** Quitar la etapa de traspaso. El proceso vuelve a terminar como digan sus
   *  demás columnas, que puede ser una despedida o el agente siguiendo. */
  async function submitEndingRemove(): Promise<void> {
    if (!selected || !endingRemoveTarget) return
    const next = await run(
      () => deleteStage(selected.id, endingRemoveTarget.id),
      'Quitamos la etapa de traspaso.',
    )
    if (next) setEndingRemoveTarget(null)
  }

  return (
    <section data-testid="pipelines-section" className="flex flex-col gap-5">
      {/* La tira del AGENTE (F4-2). Abre la página porque es lo que no cambia
          con el proceso que se elija: su saludo, su consentimiento y lo que ya
          sabe atender. Debajo empieza la zona de UN proceso, y todo lo que va
          ahí sigue al selector. */}
      <AgentStrip
        botId={agents[0]?.id ?? ''}
        agentName={agents[0]?.name ?? ''}
        agentCount={agents.length}
      />

      <div className="rounded-xl border border-line bg-surface p-5">
        <h2 className="text-[13px] font-semibold text-text">Procesos</h2>
        {/* El espejo de la frase que /asistente ya trae (F2-6b D). Las dos
            pantallas se explican una a la otra: aquí se modela el proceso, allá
            se ve el agente que sale de él. */}
        <p
          className="mt-1.5 text-[13px] leading-relaxed text-text-2"
          data-testid="pipelines-mirror"
        >
          {PIPELINES_MIRROR}{' '}
          <Link to="/asistente" className="font-semibold text-link underline">
            Ver agentes
          </Link>
        </p>
        <p className="mt-1.5 text-[12px] leading-relaxed text-text-2" data-testid="pipelines-intro">
          Cada proceso es el recorrido de tus prospectos, en etapas. En las etapas atendidas por el
          Agente, la IA conversa y avanza los prospectos automáticamente. Cuando una etapa tiene
          como responsable a tu equipo o a una persona, al llegar ahí la conversación pasa a manos
          humanas, según tus reglas de asignación en Operación. Las etapas Ganada y Perdida cierran
          el recorrido. Todo proceso lleva al menos una etapa abierta, y puede tener varias etapas
          finales, ganadas o perdidas.
        </p>

        <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-end">
          <FormField label="Proceso" htmlFor="pipeline-select" className="flex-1">
            <Select
              id="pipeline-select"
              value={selected?.id ?? ''}
              onChange={(e) => setSelectedId(e.target.value)}
              options={pipelines.map((p) => ({
                value: p.id,
                label: p.isDefault ? `${p.name} (predeterminado)` : p.name,
              }))}
            />
          </FormField>
          <div className="flex flex-wrap gap-2">
            <Button variant="secondary" onClick={() => setCreateOpen(true)} disabled={busy}>
              Nuevo proceso
            </Button>
            <Button
              variant="secondary"
              disabled={!selected || busy}
              onClick={() => {
                setRenameValue(selected?.name ?? '')
                setRenameOpen(true)
              }}
            >
              Renombrar
            </Button>
            {selected && !selected.isDefault && (
              <Button variant="secondary" disabled={busy} onClick={() => void makeDefault()}>
                Hacer predeterminado
              </Button>
            )}
            {/* Offered even when a rule refuses it: the confirm explains the
                rule, which is where a tenant goes looking for it. */}
            <Button
              variant="ghost"
              disabled={!selected || busy}
              {...(deleteBlocker === null ? {} : { title: deleteBlocker })}
              onClick={() => {
                setDeleteConfirm('')
                setDeleteOpen(true)
              }}
            >
              Eliminar proceso
            </Button>
          </div>
        </div>

        {/* CÓMO ENTRA LA GENTE A ESTE PROCESO (F6-2). La etiqueta (F4-5) y el
            significado (F5-2) son las dos mitades de la misma puerta y se leían
            como dos campos sueltos, sin decir en pantalla cómo se reparten el
            trabajo (Juan metió una LISTA de palabras clave en la etiqueta, que
            es el trabajo del significado). Van bajo un solo título, con una
            línea que lo explica arriba y el orden del argumento debajo: primero
            la ETIQUETA, que es lo que el agente ofrece en su menú; luego el
            SIGNIFICADO, que es lo que entiende por debajo. Cada campo conserva
            su propia ayuda; la línea de arriba sólo los enmarca. */}
        {selected && (
          <div className="mt-5 border-t border-line pt-4" data-testid="pipeline-entry-group">
            <h3 className="text-[12px] font-semibold text-text">{ENTRY_SECTION_TITLE}</h3>
            <p
              className="mt-1.5 text-[12px] leading-relaxed text-text-2"
              data-testid="pipeline-entry-intro"
            >
              {ENTRY_SECTION_INTRO}
            </p>

            {/* CÓMO LO RECONOCE EL AGENTE (F4-5). En blanco no es un hueco, es
                "derívala del nombre", así que el placeholder ENSEÑA cuál sería
                en vez de escribirla dentro del campo: escrita, renombrar el
                proceso dejaría de moverla. La frase derivada sale de
                `captureCategoryLabelFor`, la MISMA función que compila el menú
                del agente (`@vysto/shared/compiler`). Una copia aquí prometería
                una frase distinta de la que el agente realmente reconoce. */}
            <div className="mt-4" data-testid="pipeline-entry-label">
              <FormField
                label={ENTRY_LABEL_LABEL}
                htmlFor="pipeline-entry-label-input"
                required="optional"
                helper={ENTRY_LABEL_HELPER}
                className="sm:max-w-md"
              >
                <Input
                  id="pipeline-entry-label-input"
                  value={entryLabelValueOf(selected)}
                  maxLength={ENTRY_LABEL_MAX}
                  disabled={busy}
                  placeholder={captureCategoryLabelFor(selected.name)}
                  data-testid="pipeline-entry-label-input"
                  onChange={(e) =>
                    setEntryLabelDrafts((prev) => ({ ...prev, [selected.id]: e.target.value }))
                  }
                  onBlur={() => void saveEntryLabel(selected)}
                />
              </FormField>
              {/* La forma se avisa mientras se escribe, sin bloquear (U1b): el
                  guardado sigue siendo del cliente, pero una lista de comas o
                  un párrafo se ven rotos en el menú y nada lo decía. */}
              {entryLabelShapeWarns(entryLabelValueOf(selected)) && (
                <p
                  className="mt-1.5 text-[11.5px] leading-relaxed text-warn sm:max-w-md"
                  data-testid="pipeline-entry-label-warning"
                >
                  {ENTRY_LABEL_SHAPE_WARNING}
                </p>
              )}
            </div>

            {/* QUÉ SIGNIFICA ESTE PROCESO (F5-2). Son DOS controles porque hacen
                dos trabajos: la etiqueta es lo que el agente DICE, el
                significado es lo que ENTIENDE. En blanco no es un hueco: el
                agente lo reconoce por su etiqueta, que es lo que hacía antes,
                así que el placeholder dice QUÉ escribir aquí, con el nombre del
                proceso puesto donde va. Textarea y no Input: un significado
                enumera de qué trata el proceso, y un renglón de una línea
                escondería la mitad de lo que el cliente acaba de escribir. */}
            <div className="mt-4" data-testid="pipeline-meaning">
              <FormField
                label={MEANING_LABEL}
                htmlFor="pipeline-meaning-input"
                required="optional"
                helper={MEANING_HELPER}
                className="sm:max-w-md"
              >
                <Textarea
                  id="pipeline-meaning-input"
                  autoGrow
                  showCount
                  value={meaningValueOf(selected)}
                  maxLength={MEANING_MAX}
                  disabled={busy}
                  placeholder={`De qué trata "${selected.name}" y con qué palabras lo pide la gente.`}
                  data-testid="pipeline-meaning-input"
                  onChange={(e) =>
                    setMeaningDrafts((prev) => ({ ...prev, [selected.id]: e.target.value }))
                  }
                  onBlur={() => void saveMeaning(selected)}
                />
              </FormField>
            </div>

            {/* EL PROBADOR (W4'-7). Cierra la puerta que este grupo abre: se
                escribe una frase como la escribiría un cliente y el
                clasificador REAL — el mismo que corre en una conversación,
                sobre el mismo menú compilado — contesta a dónde entraría. El
                resultado se pinta con la oración del servidor, verbatim. */}
            <div className="mt-4" data-testid="pipeline-route-tester">
              <FormField
                label={TESTER_LABEL}
                htmlFor="pipeline-route-tester-input"
                className="sm:max-w-md"
              >
                <div className="flex items-start gap-2">
                  <Input
                    id="pipeline-route-tester-input"
                    value={testerText}
                    maxLength={500}
                    placeholder="Escribe un mensaje como lo escribiría un cliente."
                    data-testid="pipeline-route-tester-input"
                    onChange={(e) => setTesterText(e.target.value)}
                  />
                  <Button
                    variant="secondary"
                    disabled={testerText.trim() === '' || testerBusy}
                    onClick={() => void probarFrase()}
                  >
                    Probar
                  </Button>
                </div>
              </FormField>
              {testerResult !== null && (
                <p
                  className="mt-1.5 text-[12px] leading-relaxed text-text-2 sm:max-w-md"
                  data-testid="pipeline-route-tester-result"
                >
                  {testerResult}
                </p>
              )}
            </div>
          </div>
        )}

        {/* ¿CÓMO TERMINA? (F4-6). Vive pegado a "Cómo lo reconoce el agente"
            porque son las dos puntas del mismo recorrido: por dónde se entra al
            proceso y qué pasa cuando el agente termina. El valor se DERIVA de
            las etapas — no hay ajuste que guardar — y el botón escribe o quita
            la etapa que lo decide, por la ruta de etapas de siempre. */}
        {selected && (
          <div className="mt-5 border-t border-line pt-4" data-testid="pipeline-ending">
            <h3 className="text-[12px] font-semibold text-text">{ENDING_TITLE}</h3>
            {/* TRES FRASES POSIBLES, y sólo una es verdad a la vez: el proceso
                donde el agente no captura nada, el que dejó su traspaso fuera
                del recorrido, y el final derivado de siempre. Las dos primeras
                no ofrecen botón: no hay final que configurar, o lo que hay que
                hacer es mover una columna que ya existe. */}
            <p
              className="mt-1.5 text-[12px] leading-relaxed text-text-2"
              data-testid="pipeline-ending-line"
            >
              {ending.capturesNothing
                ? ENDING_NO_CAPTURE
                : ending.strandedHandoffStage
                  ? strandedHandoffLine(ending.strandedHandoffStage.name)
                  : endingLine(ending.terminal, ending.handoffStage?.name ?? '')}
            </p>
            {/* LA TARJETA NO ES LA CONVERSACIÓN (F6-3). Contexto de todo el
                bloque, no atado a un final: vale para los tres terminales. No se
                dice cuando el proceso no captura nada, porque ahí no hay "cuando
                el agente termina de capturar" que contar. */}
            {!ending.capturesNothing && (
              <p
                className="mt-1.5 text-[11.5px] leading-relaxed text-text-3"
                data-testid="pipeline-ending-vs-card"
              >
                {ENDING_VS_CARD}
              </p>
            )}
            {!ending.capturesNothing &&
              !ending.strandedHandoffStage &&
              ending.terminal === 'fin_ok' && (
                <p
                  className="mt-1.5 text-[11.5px] leading-relaxed text-text-3"
                  data-testid="pipeline-ending-hint"
                >
                  {ENDING_CHANGED_IN_STAGES}
                </p>
              )}
            {ending.midWindowStage && (
              <p
                className="mt-1.5 text-[11.5px] leading-relaxed text-warn"
                data-testid="pipeline-ending-mid-window"
              >
                {midWindowWarning(ending.midWindowStage.name, ownerWordsOf(ending.midWindowStage))}
              </p>
            )}
            {/* `fin_ok` no ofrece botón: no hay nada que quitar, y agregar el
                traspaso desde aquí escondería que este proceso todavía no tiene
                ninguna columna final. Se cambia con las etapas, abajo, y desde
                F4-10 el renglón de arriba lo dice. */}
            {!ending.capturesNothing && ending.terminal === 'pasar' && ending.handoffStage && (
              <Button
                variant="secondary"
                size="sm"
                className="mt-3"
                disabled={busy}
                data-testid="pipeline-ending-remove"
                onClick={() => setEndingRemoveTarget(ending.handoffStage)}
              >
                Quitar la etapa de traspaso
              </Button>
            )}
            {!ending.capturesNothing &&
              ending.terminal === 'cierre' &&
              !ending.strandedHandoffStage && (
                <Button
                  variant="secondary"
                  size="sm"
                  className="mt-3"
                  disabled={busy}
                  data-testid="pipeline-ending-add"
                  onClick={() => {
                    setEndingStageName(HANDOFF_STAGE_DEFAULT_NAME)
                    setEndingAddOpen(true)
                  }}
                >
                  Agregar etapa de traspaso
                </Button>
              )}
          </div>
        )}

        {/* Quién ve los prospectos de este proceso (F18). Vive en el bloque del
            proceso porque es un hecho DE ESTE proceso: un espacio puede tener
            un proceso amurallado y otro compartido. Las conversaciones siguen
            teniendo su propia visibilidad en Operación. */}
        {selected && (
          <div className="mt-4" data-testid="pipeline-visibility">
            <FormField
              label="Visibilidad de prospectos"
              htmlFor="pipeline-visibility-select"
              className="sm:max-w-md"
            >
              <Select
                id="pipeline-visibility-select"
                disabled={busy}
                value={selected.visibilityPolicy}
                options={(Object.keys(VISIBILITY_LABEL) as PipelineVisibilityPolicy[]).map(
                  (value) => ({ value, label: VISIBILITY_LABEL[value] }),
                )}
                onChange={(e) => void setVisibility(e.target.value as PipelineVisibilityPolicy)}
              />
              <p className="mt-2 text-[11px] text-text-3">
                {VISIBILITY_HELP[selected.visibilityPolicy]}
              </p>
            </FormField>
          </div>
        )}

        {/* El comportamiento del proceso. Vive aquí, y no dentro de una etapa,
            porque es un hecho de ESTE proceso: solo una etapa puede ser la de
            cita agendada, y puede no haber ninguna. Guarda por el mismo PATCH
            de etapa de siempre. (F20: la etapa de captura ya no se elige; los
            prospectos nuevos entran a la primera etapa abierta.) */}
        {selected && openStages.length > 0 && (
          <div className="mt-5 border-t border-line pt-4" data-testid="pipeline-markers">
            <h3 className="text-[12px] font-semibold text-text">Comportamiento del proceso</h3>
            <div className="mt-3 flex flex-col gap-3 sm:flex-row">
              <FormField
                label="Etapa de cita agendada"
                htmlFor="pipeline-commitment-stage"
                helper={COMMITMENT_DESCRIPTION}
                className="flex-1"
              >
                <Select
                  id="pipeline-commitment-stage"
                  // D22-9: con una etapa que AGENDA, este Select deja de ser una
                  // decisión. La base exige que la columna que agenda sea la que
                  // lleva la marca, así que dos controles sobre el mismo hecho
                  // sólo pueden servir para que se contradigan: el que manda es
                  // el que dice qué pasa en la etapa, y aquí se muestra el
                  // resultado.
                  disabled={busy || agendaStage !== undefined}
                  value={agendaStage?.id ?? commitmentStage?.id ?? NO_STAGE}
                  options={[
                    { value: NO_STAGE, label: 'Ninguna' },
                    ...openStages.map((s) => ({ value: s.id, label: s.name })),
                  ]}
                  onChange={(e) => void setCommitment(e.target.value)}
                />
                {agendaStage !== undefined ? (
                  <p
                    className="mt-2 text-[11px] text-text-3"
                    data-testid="pipeline-commitment-follows-agenda"
                  >
                    {COMMITMENT_FOLLOWS_AGENDA}
                  </p>
                ) : (
                  !commitmentStage && (
                    <p className="mt-2 text-[11px] text-text-3">{NO_COMMITMENT_HINT}</p>
                  )
                )}
              </FormField>
            </div>
          </div>
        )}
      </div>

      {/* Datos del proceso (CP3 K). Vive entre el comportamiento y las etapas
          porque ese es el orden en que se decide: qué lleva el proceso, y
          luego qué exige cada etapa de lo que lleva. Las DEFINICIONES siguen
          siendo del espacio; lo que se elige aquí es el subconjunto que este
          proceso captura, y desde CP3 L el menú de cada fila alcanza también la
          definición misma (esta es su única casa). */}
      <div className="rounded-xl border border-line bg-surface p-5" data-testid="pipeline-fields">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-[13px] font-semibold text-text">Datos del proceso</h2>
            <p className="mt-1 text-[12px] leading-relaxed text-text-2" data-testid="ficha-intro">
              {FICHA_INTRO}
            </p>
          </div>
          <div className="flex flex-none flex-wrap items-center gap-2">
            <Button
              variant="secondary"
              disabled={!selected || busy || !libraryReady}
              onClick={() => setFieldPickerOpen(true)}
            >
              Agregar dato
            </Button>
          </div>
        </div>

        {/* El catálogo decide qué dice cada fila (si está oculto) y qué acciones
            ofrece, así que la lista espera a tenerlo en lugar de dibujarse con
            la mitad de la verdad. */}
        {fieldsError ? (
          <div className="mt-4" data-testid="ficha-fields-error">
            <QueryError
              message="No pudimos cargar tu catálogo de datos."
              onRetry={() => void refetchFields()}
            />
          </div>
        ) : !libraryReady ? (
          <div className="mt-4" data-testid="ficha-fields-loading">
            <Spinner label="Cargando datos…" />
          </div>
        ) : (
          <>
            {ficha.length === 0 ? (
              <p className="mt-4 text-[12px] text-text-2" data-testid="ficha-empty">
                {FICHA_EMPTY}
              </p>
            ) : (
              <ul className="mt-4 flex flex-col gap-2">
                {ficha.map((f, index) => (
                  <li
                    key={f.id}
                    data-testid={`ficha-row-${f.key}`}
                    className="grid grid-cols-[auto_minmax(0,1fr)] items-center gap-x-3 gap-y-2 rounded-lg border border-line p-3 sm:grid-cols-[auto_minmax(0,1fr)_auto]"
                  >
                    {/* El orden del dato (F2-6b A). Dos flechas de verdad bajo la
                    palabra que dice qué son: los glifos "↑" y "↓" dentro de un
                    botón fantasma no se entendían. */}
                    <ReorderControl
                      label={f.label}
                      upDisabled={index === 0 || busy}
                      downDisabled={index === ficha.length - 1 || busy}
                      onMoveUp={() => void moveField(f, -1)}
                      onMoveDown={() => void moveField(f, 1)}
                    />

                    <div className="min-w-0">
                      <p className="truncate text-[12.5px] font-semibold text-text">{f.label}</p>
                      <p
                        className="mt-0.5 truncate text-[11.5px] text-text-2"
                        data-testid={`ficha-meta-${f.key}`}
                      >
                        {fieldMeta(f)}
                      </p>
                    </div>

                    {/* Dos alcances, dos controles: "Quitar" es de ESTE proceso, el
                    menú es de la BIBLIOTECA del espacio (renombrar, opciones,
                    ocultar, eliminar) y toca a todos los procesos.
                    El interés no lleva "Quitar" (su razón se lee en la meta de
                    la fila), y el menú sin acciones no dibuja disparador: un
                    botón que abre una lista vacía es una puerta pintada. */}
                    <div className="col-span-2 flex flex-none items-center justify-end gap-1 sm:col-span-1">
                      {/* Ver lo guardado, sin salir de la fila (F2-6b B): el tipo en
                      palabras, las opciones ENTERAS y en qué otros procesos
                      vive el dato. Editar sigue en el menú: un segundo editor
                      sería otra manera de hacer lo mismo, mal. */}
                      <Button
                        variant="ghost"
                        size="sm"
                        aria-label={
                          openDetails.includes(f.key)
                            ? `Ocultar detalle de ${f.label}`
                            : `Ver detalle de ${f.label}`
                        }
                        aria-expanded={openDetails.includes(f.key)}
                        aria-controls={`ficha-detail-${f.key}`}
                        data-testid={`ficha-detail-toggle-${f.key}`}
                        onClick={() => toggleDetail(f.key)}
                      >
                        {openDetails.includes(f.key) ? 'Ocultar detalle' : 'Ver detalle'}
                      </Button>
                      {f.key !== INTEREST_KEY && (
                        <Button
                          variant="ghost"
                          size="sm"
                          disabled={busy}
                          aria-label={`Quitar ${f.label}`}
                          onClick={() => setFieldRemoveTarget(f)}
                        >
                          Quitar
                        </Button>
                      )}
                      {fieldMenuFor(f).length > 0 && (
                        <DropdownMenu
                          trigger={
                            <button
                              type="button"
                              aria-label={`Acciones para ${f.label}`}
                              className="inline-flex size-8 items-center justify-center rounded-lg text-text-3 transition-colors hover:bg-hover hover:text-text focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-brand-blue"
                            >
                              <DotsIcon />
                            </button>
                          }
                          items={fieldMenuFor(f)}
                        />
                      )}
                    </div>

                    {/* LA FRASE ES TUYA (F4-4). La pregunta de cada dato dejó de
                    ser cosa del generador: se escribe aquí, en el renglón del
                    dato, y de aquí sale el flujo. En blanco no es un hueco, es
                    "usa la generada", así que el placeholder ENSEÑA cuál sería
                    en vez de escribirla dentro del campo: escrita, un renombre
                    del dato dejaría de moverla.

                    La frase generada sale de `promptForField`, la MISMA función
                    que arma el flujo (`@vysto/shared/compiler`). Una copia aquí
                    prometería una pregunta distinta de la que el agente hace. */}
                    <div className="col-span-2 sm:col-span-3">
                      <FormField
                        label={FICHA_PROMPT_LABEL}
                        htmlFor={`ficha-prompt-${f.key}`}
                        required="optional"
                        helper={fichaPromptHelper(f)}
                      >
                        <Input
                          value={promptValueOf(f)}
                          maxLength={QUESTION_PROMPT_MAX}
                          disabled={busy}
                          placeholder={promptForField(f)}
                          data-testid={`ficha-prompt-${f.key}`}
                          onChange={(e) =>
                            setPromptDrafts((prev) => ({ ...prev, [f.id]: e.target.value }))
                          }
                          onBlur={() => void savePrompt(f)}
                        />
                      </FormField>
                    </div>

                    {openDetails.includes(f.key) && (
                      <div
                        id={`ficha-detail-${f.key}`}
                        data-testid={`ficha-detail-${f.key}`}
                        className="col-span-2 space-y-2 rounded-lg bg-field p-3 sm:col-span-3"
                      >
                        <p className="text-[11.5px] text-text-2">
                          Tipo: {DATA_TYPE_LABELS[f.dataType]}
                        </p>
                        {f.dataType === 'enum' &&
                          ((f.options ?? []).length > 0 ? (
                            <div data-testid={`ficha-options-${f.key}`}>
                              <p className="text-[11.5px] text-text-2">Opciones:</p>
                              <div className="mt-1 flex flex-wrap gap-1">
                                {(f.options ?? []).map((o) => (
                                  <Chip key={o}>{o}</Chip>
                                ))}
                              </div>
                            </div>
                          ) : (
                            <p
                              className="text-[11.5px] text-text-2"
                              data-testid={`ficha-options-${f.key}`}
                            >
                              Esta lista todavía no tiene opciones.
                            </p>
                          ))}
                        <p
                          className="text-[11.5px] text-text-2"
                          data-testid={`ficha-used-in-${f.key}`}
                        >
                          {usedInLine(f.id, selected?.id ?? '')}
                        </p>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </div>

      <StageList
        pipelineId={selected?.id ?? ''}
        stages={stages}
        members={members}
        policies={data.policies}
        ficha={ficha}
        fichaActive={fichaActive}
        fieldLabelOf={fieldLabelOf}
        busy={busy}
        run={run}
      />

      {/* Así quedaría tu agente EN ESTE PROCESO (W2-5, acotada en F4-2), y el
          Publicar que lo escribe. Cierra la zona del proceso: primero se modela
          (ficha y etapas), luego se ve lo que de ahí sale, y ahí mismo se
          publica. Lo que es del AGENTE y no de este proceso se dice arriba, en
          la tira.

          UNA SOLA HISTORIA DE PUBLICACIÓN (W4'-4b). Debajo vivía la tarjeta del
          mecanismo anterior, que publicaba el flujo de captura de un proceso a la
          vez: dos botones de publicar en la misma pantalla se leían como dos
          productos, y el cliente no tenía cómo saber cuál era el suyo. */}
      <AgentPreviewCard
        botId={agents[0]?.id ?? ''}
        processKey={selected?.key ?? ''}
        processName={selected?.name ?? ''}
      />

      {/* Nuevo proceso */}
      <Modal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        title="Nuevo proceso"
        subtitle="Empieza con tres etapas (Nuevo, Ganado, Perdido) que puedes cambiar después."
        footer={
          <>
            <Button variant="ghost" size="sm" onClick={() => setCreateOpen(false)} disabled={busy}>
              Cancelar
            </Button>
            <Button
              size="sm"
              className="ml-auto"
              disabled={busy}
              onClick={() => void submitCreate()}
            >
              {busy ? 'Creando…' : 'Crear proceso'}
            </Button>
          </>
        }
      >
        <FormField label="Nombre" htmlFor="pipeline-name">
          <Input
            id="pipeline-name"
            placeholder="Postventa"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
          />
        </FormField>
      </Modal>

      {/* Agregar la etapa de traspaso (F4-6). Es una etapa como cualquier otra
          de este proceso, así que se nombra: va a vivir en el tablero. */}
      <Modal
        open={endingAddOpen}
        onClose={() => setEndingAddOpen(false)}
        title="Agregar etapa de traspaso"
        subtitle="Al terminar de capturar, el agente pasará la conversación con una persona."
        footer={
          <>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setEndingAddOpen(false)}
              disabled={busy}
            >
              Cancelar
            </Button>
            <Button
              size="sm"
              className="ml-auto"
              disabled={busy}
              onClick={() => void submitEndingAdd()}
            >
              {busy ? 'Agregando…' : 'Agregar etapa'}
            </Button>
          </>
        }
      >
        <FormField
          label="Nombre de la etapa"
          htmlFor="pipeline-ending-stage-name"
          helper="La etapa se agrega justo después de las etapas donde el agente captura."
        >
          <Input
            id="pipeline-ending-stage-name"
            value={endingStageName}
            maxLength={80}
            data-testid="pipeline-ending-stage-name"
            onChange={(e) => setEndingStageName(e.target.value)}
          />
        </FormField>
      </Modal>

      {/* Quitar la etapa de traspaso. Se pregunta antes: es una etapa del
          tablero, no un interruptor. */}
      <Modal
        open={endingRemoveTarget !== null}
        onClose={() => setEndingRemoveTarget(null)}
        role="alertdialog"
        title="Quitar la etapa de traspaso"
        subtitle={
          endingRemoveTarget
            ? `Se elimina la etapa "${endingRemoveTarget.name}" de este proceso.`
            : ''
        }
        footer={
          <>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setEndingRemoveTarget(null)}
              disabled={busy}
            >
              Cancelar
            </Button>
            <Button
              variant="destructive"
              size="sm"
              className="ml-auto"
              disabled={busy}
              data-testid="pipeline-ending-remove-confirm"
              onClick={() => void submitEndingRemove()}
            >
              Quitar la etapa
            </Button>
          </>
        }
      >
        <p className="text-[12.5px] leading-relaxed text-text-2">
          Al terminar de capturar, el agente dejará de pasar la conversación con una persona. Si la
          etapa tiene prospectos, muévelos antes.
        </p>
      </Modal>

      {/* Renombrar */}
      <Modal
        open={renameOpen}
        onClose={() => setRenameOpen(false)}
        title="Renombrar proceso"
        subtitle="Solo cambia cómo se llama; las etapas y los prospectos siguen igual."
        footer={
          <>
            <Button variant="ghost" size="sm" onClick={() => setRenameOpen(false)} disabled={busy}>
              Cancelar
            </Button>
            <Button
              size="sm"
              className="ml-auto"
              disabled={busy}
              onClick={() => void submitRename()}
            >
              Guardar
            </Button>
          </>
        }
      >
        <FormField label="Nombre" htmlFor="pipeline-rename">
          <Input
            id="pipeline-rename"
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
          />
        </FormField>
      </Modal>

      {/* Eliminar proceso — typed-name confirm, the house destructive pattern,
          or the rule that refuses it. */}
      <Modal
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        role="alertdialog"
        title="Eliminar proceso"
        subtitle={selected ? `Se elimina "${selected.name}" y todas sus etapas.` : ''}
        footer={
          <>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setDeleteOpen(false)}
              disabled={busy}
            >
              {deleteBlocker === null ? 'Cancelar' : 'Entendido'}
            </Button>
            <Button
              variant="destructive"
              size="sm"
              className="ml-auto"
              disabled={
                busy || !selected || deleteBlocker !== null || deleteConfirm !== selected.name
              }
              onClick={() => void submitDelete()}
            >
              Eliminar definitivamente
            </Button>
          </>
        }
      >
        {deleteBlocker !== null ? (
          <p
            className="text-[12.5px] leading-relaxed text-text-2"
            data-testid="pipeline-delete-blocker"
          >
            No puedes eliminar este proceso: {deleteBlocker}
          </p>
        ) : (
          <div className="flex flex-col gap-3.5">
            <p className="text-[12.5px] leading-relaxed text-text-2">
              Esta acción no se puede deshacer. El menú del agente seguirá ofreciendo este proceso
              hasta tu siguiente Publicar; al publicar, su opción y su flujo salen del agente.
            </p>
            <FormField
              label={`Escribe "${selected?.name ?? ''}" para confirmar`}
              htmlFor="pipeline-delete-confirm"
            >
              <Input
                id="pipeline-delete-confirm"
                value={deleteConfirm}
                onChange={(e) => setDeleteConfirm(e.target.value)}
                placeholder={selected?.name ?? ''}
              />
            </FormField>
          </div>
        )}
      </Modal>

      {/* Agregar dato — la biblioteca del espacio primero (lo normal es que el
          dato ya exista y solo falte en ESTE proceso), y crear uno nuevo como
          la salida cuando no está. Las dos puertas en el mismo diálogo porque
          la pregunta es una sola: qué dato lleva este proceso. */}
      <Modal
        open={fieldPickerOpen}
        onClose={closeFieldPicker}
        title="Agregar dato al proceso"
        subtitle={selected ? selected.name : ''}
        footer={
          <>
            <Button variant="ghost" size="sm" onClick={closeFieldPicker} disabled={busy}>
              Cancelar
            </Button>
            {fieldCreating && (
              <Button
                size="sm"
                className="ml-auto"
                disabled={busy}
                onClick={() => void submitFieldCreate()}
              >
                {busy ? 'Creando…' : 'Crear y agregar'}
              </Button>
            )}
          </>
        }
      >
        <div className="flex flex-col gap-4" data-testid="ficha-picker">
          <p className="text-[11px] leading-relaxed text-text-3">{FICHA_LIBRARY_HINT}</p>

          {!fieldCreating && (
            <>
              {/* Tres razones distintas para una lista vacía, dichas distinto:
                  todavía no llegó el catálogo, el catálogo no tiene nada, o
                  este proceso ya lo lleva todo. Con una sola frase, "aún estoy
                  cargando" se leía como "no hay nada que agregar". */}
              {!libraryReady ? (
                <p className="text-[12px] text-text-2" data-testid="ficha-picker-empty">
                  Cargando...
                </p>
              ) : activeLeadFields.length === 0 ? (
                <p className="text-[12px] text-text-2" data-testid="ficha-picker-empty">
                  Aún no tienes datos en tu catálogo. Crea el primero aquí.
                </p>
              ) : libraryAvailable.length === 0 ? (
                <p className="text-[12px] text-text-2" data-testid="ficha-picker-empty">
                  Este proceso ya lleva todos los datos de tu catálogo. Crea uno nuevo para agregar
                  algo más.
                </p>
              ) : (
                <ul className="flex flex-col gap-1.5">
                  {/* La fila DICE lo que hace el clic ("Agregar", a la derecha,
                      siempre visible) y qué es el dato antes de agregarlo (su
                      tipo, y cuántas opciones si es lista). Antes era una fila
                      muda: nada indicaba que apretarla lo metiera al proceso, y
                      no había manera de ver qué contenía sin agregarlo. */}
                  {libraryAvailable.map((f) => (
                    <li key={f.id} className="flex items-center gap-1">
                      <Button
                        variant="secondary"
                        size="sm"
                        className="min-w-0 flex-1 justify-start [&>span]:w-full"
                        disabled={busy}
                        data-testid={`ficha-picker-row-${f.key}`}
                        onClick={() => void addFieldToFicha(f.id)}
                      >
                        <span className="flex w-full items-center gap-3 text-left">
                          <span className="min-w-0">
                            <span className="block truncate">{f.label}</span>
                            <span className="mt-0.5 block truncate font-normal text-text-3">
                              {libraryFieldMeta(f)}
                            </span>
                            {/* Y en qué OTROS procesos vive ya (F2-6b B/D5):
                                agregarlo aquí no lo inventa, lo comparte, y
                                editarlo después toca a todos. */}
                            <span
                              className="mt-0.5 block truncate font-normal text-text-3"
                              data-testid={`ficha-picker-used-in-${f.key}`}
                            >
                              {usedInLine(f.id, '')}
                            </span>
                          </span>
                          <span className="ml-auto flex-none font-normal text-text-3">Agregar</span>
                        </span>
                      </Button>
                      {/* La ÚNICA salida para un dato viejo. Un dato que no está
                          en la ficha de ningún proceso no tiene fila con menú en
                          ninguna parte, así que aquí, donde se le ve por última
                          vez, es donde se puede borrar. Un campo del sistema no
                          lo lleva: el API lo rechaza y ofrecerlo sería una
                          puerta pintada. */}
                      {!f.managed && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="flex-none"
                          disabled={busy}
                          aria-label={`Eliminar ${f.label} del catálogo`}
                          data-testid={`ficha-picker-delete-${f.key}`}
                          onClick={() => setFieldDeleteTarget(f)}
                        >
                          Eliminar
                        </Button>
                      )}
                    </li>
                  ))}
                </ul>
              )}
              <Button
                variant="ghost"
                size="sm"
                disabled={busy}
                onClick={() => setFieldCreating(true)}
              >
                Crear nuevo dato
              </Button>
            </>
          )}

          {fieldCreating && (
            <div className="flex flex-col gap-4">
              <FormField label="Nombre" htmlFor="ficha-new-field-label">
                <Input
                  id="ficha-new-field-label"
                  placeholder="Aseguradora"
                  value={newFieldLabel}
                  onChange={(e) => setNewFieldLabel(e.target.value)}
                />
              </FormField>
              <FormField label="Tipo" htmlFor="ficha-new-field-type">
                <Select
                  id="ficha-new-field-type"
                  value={newFieldType}
                  options={(Object.keys(DATA_TYPE_LABELS) as FieldDataType[]).map((value) => ({
                    value,
                    label: DATA_TYPE_LABELS[value],
                  }))}
                  onChange={(e) => setNewFieldType(e.target.value as FieldDataType)}
                />
              </FormField>
              {newFieldType === 'enum' && (
                <OptionsSourceSelect
                  id="ficha-new-field-options-source"
                  value={newFieldSource}
                  onChange={setNewFieldSource}
                />
              )}
              {newFieldType === 'enum' &&
                (newFieldSource !== 'lista' ? (
                  // El dato todavía no existe, así que no hay lista resuelta
                  // que enseñar: lo que se dice es de dónde va a salir.
                  <SourcedOptionsList source={newFieldSource} options={null} />
                ) : (
                  <FormField
                    label="Opciones"
                    htmlFor="ficha-new-field-options"
                    helper="Una por línea."
                  >
                    <Textarea
                      id="ficha-new-field-options"
                      placeholder={'GNP\nAXA\nMetlife'}
                      value={newFieldOptions}
                      onChange={(e) => setNewFieldOptions(e.target.value)}
                    />
                  </FormField>
                ))}
            </div>
          )}
        </div>
      </Modal>

      {/* Quitar dato — la advertencia va DENTRO de la confirmación, con los
          nombres de las etapas que dejan de exigirlo: el recorte lo hace el
          API en la misma transacción, así que decirlo después no sería un
          aviso sino una noticia. */}
      <Modal
        open={fieldRemoveTarget !== null}
        onClose={() => setFieldRemoveTarget(null)}
        role="alertdialog"
        title="Quitar dato del proceso"
        subtitle={fieldRemoveTarget ? fieldRemoveTarget.label : ''}
        footer={
          <>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setFieldRemoveTarget(null)}
              disabled={busy}
            >
              Cancelar
            </Button>
            <Button
              variant="destructive"
              size="sm"
              className="ml-auto"
              disabled={busy}
              onClick={() => void submitFieldRemove()}
            >
              Quitar dato
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-3">
          <p className="text-[12.5px] leading-relaxed text-text-2">
            {fieldRemoveTarget
              ? `Los prospectos de este proceso dejan de llevar "${fieldRemoveTarget.label}". El dato sigue en tu catálogo y en los procesos que lo usen, y lo ya capturado no se borra.`
              : ''}
          </p>
          {fieldRemoveLosers.length > 0 && (
            <p
              className="text-[12.5px] leading-relaxed text-text-2"
              data-testid="ficha-remove-warning"
            >
              También dejará de pedirse y de ser requerido en:{' '}
              {fieldRemoveLosers.map((s) => s.name).join(', ')}.
            </p>
          )}
        </div>
      </Modal>

      {/* Editar el dato en la BIBLIOTECA (CP3 L). Estas acciones vivían en la
          pestaña Campos, que ahora es solo de personas; se mudaron aquí con sus
          mismas guardas para que nada de lo que se podía hacer se perdiera. */}
      <Modal
        open={fieldEditTarget !== null}
        onClose={() => setFieldEditTarget(null)}
        title={fieldEditTarget?.managed ? 'Editar opciones' : 'Editar dato'}
        subtitle={fieldEditTarget ? fieldEditTarget.key : ''}
        footer={
          <>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setFieldEditTarget(null)}
              disabled={busy}
            >
              Cancelar
            </Button>
            <Button
              size="sm"
              className="ml-auto"
              disabled={busy}
              onClick={() => void submitFieldEdit()}
            >
              {busy ? 'Guardando…' : 'Guardar'}
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-4" data-testid="ficha-field-editor">
          <p className="text-[11px] leading-relaxed text-text-3">
            Este dato es de la biblioteca del espacio: lo que cambies aquí lo ven todos los procesos
            que lo llevan.
          </p>
          {fieldEditTarget?.managed ? (
            <p className="text-[12.5px] leading-relaxed text-text-2">
              Los datos del sistema conservan su nombre y su tipo: los usan tus flujos, tus reglas y
              los requisitos de tus etapas. Sus opciones son tuyas, cámbialas cuando quieras.
            </p>
          ) : (
            <FormField label="Nombre" htmlFor="ficha-edit-field-label">
              <Input
                id="ficha-edit-field-label"
                value={editFieldLabel}
                onChange={(e) => setEditFieldLabel(e.target.value)}
              />
            </FormField>
          )}
          {fieldEditTarget?.data_type === 'enum' && (
            <OptionsSourceSelect
              id="ficha-edit-field-options-source"
              value={editFieldSource}
              onChange={setEditFieldSource}
            />
          )}
          {fieldEditTarget?.data_type === 'enum' &&
            (editFieldSource !== 'lista' ? (
              <SourcedOptionsList
                source={editFieldSource}
                // Solo se puede enseñar la lista que el API ya resolvió: si en
                // este diálogo se acaba de elegir OTRA fuente, la que llegó es
                // la de la anterior y no la de esta.
                options={
                  editFieldSource === fieldEditTarget.options_source
                    ? (fieldEditTarget.options ?? [])
                    : null
                }
              />
            ) : (
              <FormField
                label="Opciones"
                htmlFor="ficha-edit-field-options"
                helper="Una por línea."
              >
                <Textarea
                  id="ficha-edit-field-options"
                  value={editFieldOptions}
                  onChange={(e) => setEditFieldOptions(e.target.value)}
                />
              </FormField>
            ))}
        </div>
      </Modal>

      {/* Eliminar el dato de la biblioteca — distinto de quitarlo del proceso, y
          por eso lo dice con todas sus letras antes de pedir el clic. */}
      <Modal
        open={fieldDeleteTarget !== null}
        onClose={() => setFieldDeleteTarget(null)}
        role="alertdialog"
        title="Eliminar dato del catálogo"
        subtitle={fieldDeleteTarget ? fieldDeleteTarget.label : ''}
        footer={
          <>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setFieldDeleteTarget(null)}
              disabled={busy}
            >
              Cancelar
            </Button>
            <Button
              variant="destructive"
              size="sm"
              className="ml-auto"
              disabled={busy}
              onClick={() => void submitFieldDelete()}
            >
              {busy ? 'Eliminando…' : 'Eliminar dato'}
            </Button>
          </>
        }
      >
        <p className="text-[12.5px] leading-relaxed text-text-2">
          {fieldDeleteTarget
            ? `Se borra "${fieldDeleteTarget.label}" de TODOS tus procesos y con él los valores guardados en las fichas de tus prospectos. Esta acción no se puede deshacer.`
            : ''}
        </p>
      </Modal>
    </section>
  )
}
